<template>
  <div class="orders-page py-3">
    <header class="orders-toolbar">
      <div class="orders-toolbar__title">
        <span class="orders-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Orders</h1>
          <p>Search and manage order listings</p>
        </div>
      </div>
      <div class="orders-toolbar__actions">
        <CButton
          v-if="canExportCurrentTab"
          color="primary"
          size="sm"
          :disabled="isLoading"
          @click="downloadTabExport"
        >
          Export CSV
        </CButton>
        <CButton
          size="sm"
          class="refresh-btn"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="reloadCurrentTab"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="refresh-btn__icon" aria-hidden="true">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          <span>{{ isLoading ? 'Refreshing…' : 'Refresh' }}</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="orders-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="orders-alert" @click.native="successMessage = ''">{{ successMessage }}</CAlert>

    <div class="orders-tabs" :class="{ 'is-scrollable': canScrollLeft || canScrollRight }">
      <button
        type="button"
        class="orders-tab-arrow orders-tab-arrow--left"
        :class="{ 'is-hidden': !canScrollLeft }"
        aria-label="Scroll tabs left"
        tabindex="-1"
        @click="scrollTabs(-1)"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      <div ref="tabBar" class="orders-tab-bar" role="tablist" @scroll="updateTabScroll">
        <button
          v-for="tab in visibleTabs"
          :key="tab.key"
          type="button"
          role="tab"
          :class="['orders-tab', { 'is-active': activeTab === tab.key }]"
          :aria-selected="activeTab === tab.key"
          @click="switchTab(tab.key)"
        >
          <span class="orders-tab__label">{{ tab.label }}</span>
        </button>
      </div>

      <button
        type="button"
        class="orders-tab-arrow orders-tab-arrow--right"
        :class="{ 'is-hidden': !canScrollRight }"
        aria-label="Scroll tabs right"
        tabindex="-1"
        @click="scrollTabs(1)"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </div>

    <section v-if="activeTab === 'search'" class="orders-section">
      <header class="orders-section__head">
        <h2>Search Filters</h2>
      </header>
      <div class="orders-section__body">
        <div class="search-grid">
          <div class="search-field">
            <label class="orders-label">No. of Bottles Range</label>
            <div class="input-pair">
              <input
                v-model.number="searchForm.minBottles"
                type="number"
                min="1"
                class="orders-input"
                placeholder="Min"
              >
              <input
                v-model.number="searchForm.maxBottles"
                type="number"
                min="1"
                class="orders-input"
                placeholder="Max"
              >
            </div>
          </div>

          <div v-if="canSwitchOrg" class="search-field">
            <label class="orders-label">Organisation</label>
            <AppDropdown
              v-model="searchForm.organisation"
              :options="organisationFilterOptions"
              searchable
              search-placeholder="Search organisation…"
              placeholder="All Organisations"
            />
          </div>

          <div v-if="!isTelesalesStaff" class="search-field">
            <label class="orders-label">Select User</label>
            <AppDropdown
              v-model="searchForm.user"
              :options="userDropdownOptions"
              searchable
              search-placeholder="Search user…"
              placeholder="Any User"
            />
          </div>

          <div v-if="!isTelesalesStaff" class="search-field">
            <label class="orders-label">Care Of</label>
            <AppDropdown
              v-model="searchForm.care_of"
              :options="careOfUserOptions"
              searchable
              search-placeholder="Search user…"
              placeholder="Any C/O User"
            />
          </div>

          <div class="search-field">
            <label class="orders-label">Product</label>
            <AppDropdown
              v-model="searchForm.product"
              :options="productFilterOptions"
              searchable
              search-placeholder="Search product…"
              placeholder="Any Product"
            />
          </div>

          <div class="search-field">
            <label class="orders-label">Order Type</label>
            <AppDropdown
              v-model="searchForm.order_type"
              :options="orderTypeDropdownOptions"
              searchable
              search-placeholder="Search type…"
              placeholder="All Types"
            />
          </div>

          <div class="search-field">
            <label class="orders-label">Payment Method</label>
            <AppDropdown
              v-model="searchForm.payment_method"
              :options="paymentMethodFilterOptions"
              searchable
              search-placeholder="Search method…"
              placeholder="Any Payment Method"
            />
          </div>

          <div class="search-field">
            <label class="orders-label">Order Status</label>
            <AppDropdown
              v-model="searchForm.order_status"
              :options="orderStatusFilterOptions"
              searchable
              search-placeholder="Search status…"
              placeholder="Any Order Status"
            />
          </div>

          <div class="search-field">
            <label class="orders-label">Shipping Status</label>
            <AppDropdown
              v-model="searchForm.shipping_status"
              :options="shippingStatusFilterOptions"
              searchable
              search-placeholder="Search status…"
              placeholder="Any Shipping Status"
            />
          </div>

          <div class="search-field">
            <label class="orders-label">Payment Status</label>
            <AppDropdown
              v-model="searchForm.payment_status"
              :options="paymentStatusFilterOptions"
              searchable
              search-placeholder="Search status…"
              placeholder="Any Payment Status"
            />
          </div>

          <div class="search-field">
            <label class="orders-label">Date Range</label>
            <DateRangePicker v-model="searchDateRange" variant="form" :presets="searchDatePresets" presets-in-popup />
          </div>

          <div class="search-field">
            <label class="orders-label">Search Filter</label>
            <AppDropdown
              v-model="searchForm.search_parameter"
              :options="searchFieldOptions"
              searchable
              search-placeholder="Search field…"
              placeholder="Select field"
            />
          </div>

          <div class="search-field">
            <label class="orders-label">Search Value</label>
            <div class="search-input-row">
              <input
                v-model.trim="searchForm.search_value"
                type="text"
                class="orders-input"
                placeholder="Type to search…"
                @keydown.enter="searchOrders"
              >
              <CButton color="primary" :disabled="isLoading" class="search-input-btn" @click="searchOrders">
                {{ isLoading ? '…' : 'Search' }}
              </CButton>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="showCareOfFilter" class="orders-section">
      <header class="orders-section__head">
        <h2>C/O Filters</h2>
      </header>
      <div class="orders-section__body">
        <div class="care-of-filter-row">
          <div class="care-of-filter-field">
            <label class="orders-label">Care Of</label>
            <AppDropdown
              v-model="careOfFilter.care_of_user"
              :options="careOfUserOptions"
              searchable
              search-placeholder="Search user…"
              placeholder="Any C/O User"
            />
          </div>
          <div class="care-of-filter-field care-of-filter-field--picker">
            <label class="orders-label">Date Range</label>
            <DateRangePicker v-model="careOfDateRange" variant="form" :presets="false" />
          </div>
          <div class="care-of-filter-field care-of-filter-field--action">
            <label class="orders-label" aria-hidden="true">&nbsp;</label>
            <CButton color="primary" :disabled="isLoading" @click="applyCareOfFilter">Apply</CButton>
          </div>
          <div class="care-of-filter-field care-of-filter-field--action">
            <label class="orders-label" aria-hidden="true">&nbsp;</label>
            <CButton color="secondary" variant="outline" :disabled="isLoading" @click="resetCareOfFilter">Reset</CButton>
          </div>
        </div>
      </div>
    </section>

    <section v-if="showDateFilter" class="orders-section">
      <div class="orders-section__body">
        <div class="date-filter-row">
          <div class="date-filter-field date-filter-field--picker">
            <label class="orders-label">Date Range</label>
            <DateRangePicker v-model="filterDateRange" variant="form" />
          </div>
          <div class="date-filter-field date-filter-field--action">
            <label class="orders-label" aria-hidden="true">&nbsp;</label>
            <CButton color="primary" :disabled="isLoading" @click="applyDateFilter">Apply</CButton>
          </div>
          <div class="date-filter-field date-filter-field--hint">
            <label class="orders-label" aria-hidden="true">&nbsp;</label>
            <small class="orders-hint">Date range is required for this tab.</small>
          </div>
        </div>
      </div>
    </section>

    <div v-if="showTrackingStats" class="summary-tiles">
      <div class="summary-tile summary-tile--pending">
        <span class="summary-tile__label">Pending</span>
        <span class="summary-tile__value">{{ trackingStats.pending }}</span>
        <span class="summary-tile__hint">Tracking IDs awaiting notification</span>
      </div>
      <div class="summary-tile summary-tile--sent">
        <span class="summary-tile__label">Sent Today</span>
        <span class="summary-tile__value">{{ trackingStats.sent_today }}</span>
        <span class="summary-tile__hint">WhatsApp notifications fired today</span>
      </div>
      <div class="summary-tile summary-tile--week">
        <span class="summary-tile__label">Sent This Week</span>
        <span class="summary-tile__value">{{ trackingStats.sent_this_week }}</span>
        <span class="summary-tile__hint">Rolling Mon–Sun total</span>
      </div>
    </div>

    <div v-if="showReturnedSummary" class="summary-tiles">
      <div class="summary-tile summary-tile--total">
        <span class="summary-tile__label">Grand Total</span>
        <span class="summary-tile__value">{{ currency(returnSummary.grand_total_amount) }}</span>
      </div>
      <div class="summary-tile summary-tile--ip">
        <span class="summary-tile__label">India Post</span>
        <span class="summary-tile__value">{{ returnSummary.ip_returned }}</span>
      </div>
      <div class="summary-tile summary-tile--amazon">
        <span class="summary-tile__label">Amazon</span>
        <span class="summary-tile__value">{{ returnSummary.amazon_returned }}</span>
      </div>
      <div class="summary-tile summary-tile--flipkart">
        <span class="summary-tile__label">Flipkart</span>
        <span class="summary-tile__value">{{ returnSummary.flipkart_returned }}</span>
      </div>
    </div>

    <div v-if="canBulkPrint" class="orders-selection-bar">
      <div class="orders-selection-bar__chips">
        <span class="orders-selection-bar__count">{{ selectedOrderIds.length }} selected</span>
        <span v-for="order in selectedOrders" :key="order.id" class="sel-chip">
          <span class="sel-chip__label">{{ order.order_number || ('#' + order.id) }}</span>
          <button
            type="button"
            class="sel-chip__remove"
            :aria-label="`Remove ${order.order_number || order.id}`"
            @click="deselectOrder(order.id)"
          >
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </span>
        <button type="button" class="sel-chip-clear" @click="clearSelection">Clear all</button>
      </div>
      <div class="orders-selection-bar__actions">
        <CButton color="primary" size="sm" class="bulk-print-btn" @click="printInvoices">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="bulk-print-btn__icon" aria-hidden="true">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
          <span>Print Invoices</span>
          <span class="bulk-print-btn__count">{{ selectedOrderIds.length }}</span>
        </CButton>
        <CButton color="primary" size="sm" class="bulk-print-btn" @click="printLabels">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="bulk-print-btn__icon" aria-hidden="true">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
            <line x1="7" y1="7" x2="7.01" y2="7" />
          </svg>
          <span>Print Labels</span>
          <span class="bulk-print-btn__count">{{ selectedOrderIds.length }}</span>
        </CButton>
      </div>
    </div>

    <section class="orders-section">
      <header class="orders-section__head">
        <h2>{{ activeTabConfig ? activeTabConfig.label : 'Orders' }}</h2>
        <span v-if="!isLoading && orders.length" class="orders-count">{{ orders.length }} result{{ orders.length === 1 ? '' : 's' }}</span>
      </header>

      <div v-if="isLoading" class="orders-section__body orders-loading">
        <CSpinner size="sm" class="mr-2" />
        <span>Loading orders…</span>
      </div>

      <div v-else-if="!orders.length" class="orders-section__body orders-empty">
        No orders found.
      </div>

      <div v-else>
        <div class="orders-table-wrap">
          <table class="orders-table">
            <thead>
              <tr>
                <th class="col-check">
                  <input
                    type="checkbox"
                    :disabled="!canSelectRows"
                    :checked="areAllOrdersSelected"
                    @change="toggleSelectAll($event.target.checked)"
                  />
                </th>
                <th>Order</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Amount</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in orders" :key="order.id" :class="['order-row', { 'order-row--cancelled': isOrderCancelled(order), 'order-row--returned': isOrderReturned(order) }]">
                <td class="col-check">
                  <input
                    type="checkbox"
                    :disabled="!canSelectRows"
                    :checked="selectedOrderIds.includes(order.id)"
                    @change="toggleSelection(order, $event.target.checked)"
                  />
                </td>

                <td class="col-order">
                  <div class="tbl-head-row">
                    <span class="tbl-id">{{ order.order_number || '—' }}</span>
                    <span :class="['status-pill', displayOrderStatusClass(order)]">{{ displayOrderStatus(order) }}</span>
                  </div>
                  <div class="tbl-sub">{{ orderDateDisplay(order) }}</div>
                  <div v-if="isAmazonOrder(order)" class="tbl-amazon-dates">
                    <span v-if="order.order_date" class="tbl-amazon-date"><span class="tbl-amazon-date__k">Invoice Date</span> {{ formatDate(order.order_date) }}</span>
                  </div>
                  <div v-if="isOrderReturned(order) && order.return_date" class="tbl-returned-on">
                    Returned {{ formatDate(order.return_date) }}
                  </div>
                  <div v-if="order.invoice_number" class="tbl-sub tbl-dim">
                    {{ order.invoice_number }}<span v-if="order.invoice_date"> · {{ formatDate(order.invoice_date) }}</span>
                  </div>
                  <div v-if="order.order_type" class="tbl-type-chip">{{ order.order_type }}</div>
                  <div v-if="soldByLabel(order) !== '-'" class="tbl-sub tbl-dim">By {{ soldByLabel(order) }}</div>
                  <div v-if="careOfLabel(order)" class="tbl-care-of" :title="`Telesales credit: ${careOfLabel(order)}`">
                    <span class="tbl-care-of__dot" aria-hidden="true"></span>
                    Care of {{ careOfLabel(order) }}
                  </div>
                  <div v-if="hasBeenUpdated(order)" class="tbl-sub tbl-dim">Last updated {{ formatDateTime(order.updated_at) }}</div>
                </td>

                <td class="col-customer">
                  <div class="tbl-name">{{ order.contact_name || '—' }}</div>
                  <div class="tbl-phone">{{ order.contact_phone || '' }}</div>
                  <div v-if="order.contact_postofficename || order.contact_district || order.contact_state" class="tbl-addr">
                    {{ [order.contact_address, order.contact_postofficename, order.contact_district, order.contact_state].filter(Boolean).join(', ') }}<span v-if="order.contact_pincode"> – {{ order.contact_pincode }}</span>
                  </div>
                </td>

                <td class="col-products">
                  <template v-if="order.order_items && order.order_items.length">
                    <div
                      v-for="(item, index) in order.order_items"
                      :key="`${order.id}-item-${index}`"
                      class="item-line"
                    >
                      <span class="item-line__main">
                        <span class="item-line__name">{{ itemLabel(item) }}</span>
                        <span
                          v-if="itemDiscountPercent(order, index)"
                          class="item-line__off"
                          :title="`${itemDiscountPercent(order, index)}% off (−${currency(itemDiscountAmount(order, index))})`"
                        >
                          — {{ itemDiscountPercent(order, index) }}% off
                        </span>
                      </span>
                      <span class="item-line__qty">×{{ item.quantity || 0 }}</span>
                    </div>
                  </template>
                  <span v-else class="tbl-sub">—</span>
                </td>

                <td class="col-charges">
                  <div class="tbl-amount">{{ currency(order.grand_total) }}</div>
                  <span
                    v-if="hasDiscount(order)"
                    class="discount-chip"
                    :title="`Discount ${discountPercentLabel(order)} (−${currency(order.discount)})`"
                  >
                    {{ discountPercentLabel(order) }} discount
                  </span>
                  <span :class="['status-pill', paymentBadgeClass(order.payment_status)]">
                    {{ paymentStatusLabel(order.payment_status) }}
                  </span>
                  <span v-if="order.payment_method" class="pay-chip">
                    <img
                      v-if="paymentMethodIcon(order.payment_method).img"
                      :src="paymentMethodIcon(order.payment_method).img"
                      class="pay-chip__logo"
                      alt=""
                      aria-hidden="true"
                    />
                    <span v-else class="pay-chip__icon" aria-hidden="true">{{ paymentMethodIcon(order.payment_method).emoji }}</span>
                    <span class="pay-chip__text">{{ paymentMethodLabel(order.payment_method) }}</span>
                  </span>
                  <div v-else class="tbl-sub">—</div>
                  <div class="tbl-sub">{{ shippingMethodLabel(order.shipping_method) }}</div>
                  <div v-if="order.payment_date" class="tbl-sub tbl-dim">{{ formatDate(order.payment_date) }}</div>
                </td>

                <td class="col-actions">
                  <CButton color="primary" size="sm" class="tbl-btn" @click="openViewModal(order)">View</CButton>
                  <CButton
                    color="dark"
                    size="sm"
                    variant="outline"
                    class="tbl-btn tbl-btn--history"
                    :disabled="historyLoadingId === order.id"
                    :title="`View past orders for ${order.contact_name || 'this customer'}`"
                    @click="openHistoryModal(order)"
                  >
                    <span v-if="historyLoadingId === order.id" class="tbl-btn__spinner"><CSpinner size="sm" /></span>
                    <span :class="{ 'is-loading-hidden': historyLoadingId === order.id }">History</span>
                  </CButton>
                  <template v-if="isPendingInvoiceTab">
                    <button
                      type="button"
                      class="tbl-btn tbl-btn--invoice"
                      :disabled="downloadingInvoiceId === order.id"
                      :title="`Download the invoice PDF for ${order.order_number || 'this order'}`"
                      @click="downloadCustomerInvoice(order)"
                    >
                      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      <span>{{ downloadingInvoiceId === order.id ? 'Preparing…' : 'Invoice' }}</span>
                    </button>
                    <button
                      type="button"
                      class="tbl-btn tbl-btn--whatsapp"
                      :class="{ 'tbl-btn--whatsapp-sent': order.invoice_sent_at }"
                      :disabled="!canSendInvoice(order)"
                      :title="order.invoice_sent_at ? `Invoice sent ${formatDateTime(order.invoice_sent_at)} — click to resend` : `Send invoice to ${order.contact_phone}`"
                      @click="sendInvoiceToCustomer(order)"
                    >
                      <svg v-if="!order.invoice_sent_at" viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479c0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.886 9.884zm8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
                      </svg>
                      <svg v-else viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      <span>{{ order.invoice_sent_at ? 'Sent' : 'Send' }}</span>
                    </button>
                  </template>
                  <CButton
                    v-if="!isPendingInvoiceTab && canCopyForAccounts"
                    color="info"
                    size="sm"
                    variant="outline"
                    class="tbl-btn"
                    :title="`Copy ${order.order_number || 'order'} details for the accounts team`"
                    @click="copyOrderForAccounts(order)"
                  >
                    {{ copiedOrderId === order.id ? 'Copied ✓' : 'Copy' }}
                  </CButton>
                  <CButton v-if="!isPendingInvoiceTab && canEditOrders" color="secondary" size="sm" variant="outline" class="tbl-btn" @click="openEditModal(order)">Edit</CButton>
                  <CButton v-if="!isPendingInvoiceTab && canCancelOrders && !isOrderCancelled(order)" color="danger" size="sm" variant="outline" class="tbl-btn" @click="openCancelConfirm(order)">Cancel</CButton>
                  <CButton v-if="!isPendingInvoiceTab && canRefundOrders && !isOrderCancelled(order)" color="warning" size="sm" variant="outline" class="tbl-btn" @click="openRefundModal(order)">Refund</CButton>
                  <button
                    v-if="!isPendingInvoiceTab && canShareTracking(order)"
                    type="button"
                    class="tbl-btn tbl-btn--whatsapp"
                    :class="{ 'tbl-btn--whatsapp-sent': order.tracking_notified_at }"
                    :title="order.tracking_notified_at ? `Already sent ${formatDateTime(order.tracking_notified_at)} — click to resend` : `Send tracking to ${order.contact_phone}`"
                    @click="sendTrackingViaWhatsapp(order)"
                  >
                    <svg v-if="!order.tracking_notified_at" viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479c0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.886 9.884zm8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
                    </svg>
                    <svg v-else viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <span>{{ order.tracking_notified_at ? 'Sent' : 'Send' }}</span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="maxPages > 1" class="orders-pagination">
          <CPagination :pages="maxPages" :active-page.sync="activePage" />
        </div>
      </div>
    </section>

    <CModal
      :show.sync="showDetailsModal"
      size="lg"
      :centered="true"
      title="View Order"
      class="view-order-modal"
    >
      <div v-if="selectedOrder" class="vo-body">
        <div class="vo-section">
          <div class="vo-row"><span class="vo-key">Order ID</span><span class="vo-val">{{ selectedOrder.order_number || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Invoice Number</span><span class="vo-val">{{ selectedOrder.invoice_number || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Marketplace Order ID</span><span class="vo-val">{{ selectedOrder.marketplace_order_id || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">GST IN</span><span class="vo-val">{{ selectedOrder.contact_gstn || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Name</span><span class="vo-val">{{ selectedOrder.contact_name || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Address</span><span class="vo-val">{{ selectedOrder.contact_address || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Post Office</span><span class="vo-val">{{ selectedOrder.contact_postofficename || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Pincode</span><span class="vo-val">{{ selectedOrder.contact_pincode || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Phone</span><span class="vo-val">{{ selectedOrder.contact_phone || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Alternate Number</span><span class="vo-val">{{ selectedOrder.contact_phone_2 || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Tracking ID</span><span class="vo-val">{{ selectedOrder.tracking_id || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Sold By</span><span class="vo-val">{{ soldByLabel(selectedOrder) }}</span></div>
        </div>

        <div class="vo-items-wrap">
          <table class="vo-items">
            <thead>
              <tr>
                <th>Product</th>
                <th>Price</th>
                <th>Quantity</th>
                <th v-if="hasLineDiscounts">Amount</th>
                <th v-if="hasLineDiscounts">Discount %</th>
                <th v-if="hasLineDiscounts">Discount Amount</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!selectedOrder.order_items || !selectedOrder.order_items.length">
                <td :colspan="hasLineDiscounts ? 7 : 4" class="vo-items__empty">No items</td>
              </tr>
              <tr v-for="(item, idx) in (selectedOrder.order_items || [])" :key="`vo-item-${idx}`">
                <td>{{ itemLabel(item) }}</td>
                <td>{{ item.price != null ? item.price : '—' }}</td>
                <td>{{ item.quantity != null ? item.quantity : '—' }}</td>
                <!-- Amount = price × qty as stored on the line (always gross —
                     the discount lives on the order, never on order_items). -->
                <td v-if="hasLineDiscounts">{{ itemRowTotal(item) }}</td>
                <td v-if="hasLineDiscounts" class="vo-items__disc">
                  <span v-if="itemDiscountPercent(selectedOrder, idx)" class="vo-disc__pct">{{ itemDiscountPercent(selectedOrder, idx) }}%</span>
                  <span v-else class="vo-disc__none">—</span>
                </td>
                <td v-if="hasLineDiscounts" class="vo-items__disc">
                  <span v-if="itemDiscountPercent(selectedOrder, idx)" class="vo-disc__amt">−{{ currency(itemDiscountAmount(selectedOrder, idx)) }}</span>
                  <span v-else class="vo-disc__none">—</span>
                </td>
                <td class="vo-items__net">{{ itemNetTotal(selectedOrder, idx, item) }}</td>
              </tr>
            </tbody>
            <!-- Order-level discount, so it reconciles the per-line split:
                 the amount columns sum to this row. Shown whenever the order
                 carries a discount, even when the per-line replay couldn't be
                 trusted (then it's the only discount row in the table). -->
            <tfoot v-if="hasOrderDiscountAmount">
              <tr class="vo-items__foot">
                <td colspan="3">Proposed discount</td>
                <template v-if="hasLineDiscounts">
                  <td>{{ selectedOrder.total }}</td>
                  <td class="vo-items__disc"><span class="vo-disc__pct">{{ discountPercentLabel(selectedOrder) }}</span></td>
                  <td class="vo-items__disc"><span class="vo-disc__amt">−{{ currency(selectedOrder.discount) }}</span></td>
                  <td class="vo-items__net">{{ afterDiscountTotal }}</td>
                </template>
                <td v-else class="vo-items__disc">
                  <span class="vo-disc__pct">{{ discountPercentLabel(selectedOrder) }}</span>
                  <span class="vo-disc__amt vo-disc__amt--gap">−{{ currency(selectedOrder.discount) }}</span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div class="vo-grid">
          <div class="vo-row"><span class="vo-key">Order Type</span><span class="vo-val">{{ selectedOrder.order_type || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Total Bottles</span><span class="vo-val">{{ totalBottlesFor(selectedOrder) }}</span></div>
          <div class="vo-row"><span class="vo-key">Total Amount</span><span class="vo-val">{{ selectedOrder.total != null ? selectedOrder.total : '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Shipping Charge</span><span class="vo-val">{{ selectedOrder.shipping_charge != null ? selectedOrder.shipping_charge : '0' }}</span></div>
          <div class="vo-row"><span class="vo-key">COD Charge</span><span class="vo-val">{{ selectedOrder.cod_charge != null ? selectedOrder.cod_charge : '0' }}</span></div>
          <div class="vo-row"><span class="vo-key">Discount</span><span class="vo-val">{{ selectedOrder.discount != null ? selectedOrder.discount : '0' }}</span></div>
          <div class="vo-row"><span class="vo-key">Discount %</span><span class="vo-val">{{ discountPercentLabel(selectedOrder) }}</span></div>
          <div class="vo-row"><span class="vo-key">Grand Total</span><span class="vo-val">{{ selectedOrder.grand_total != null ? selectedOrder.grand_total : '—' }}</span></div>
          <div class="vo-row">
            <span class="vo-key">Shipping Method</span>
            <span class="vo-val">
              <span v-if="selectedOrder.shipping_method" class="vo-pill vo-pill--shipping">{{ shippingMethodLabel(selectedOrder.shipping_method) }}</span>
              <span v-else>—</span>
            </span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Payment Method</span>
            <span class="vo-val">
              <span v-if="selectedOrder.payment_method" class="vo-pill vo-pill--payment">{{ paymentMethodLabel(selectedOrder.payment_method) }}</span>
              <span v-else>—</span>
            </span>
          </div>
          <div class="vo-row">
            <span class="vo-key">Payment status</span>
            <span class="vo-val">
              <span :class="['vo-pill', paymentBadgeClass(selectedOrder.payment_status)]">{{ paymentStatusLabel(selectedOrder.payment_status) }}</span>
            </span>
          </div>
          <div class="vo-row"><span class="vo-key">Payment Date</span><span class="vo-val">{{ formatDate(selectedOrder.payment_date) }}</span></div>
          <div class="vo-row"><span class="vo-key">Shipping Status</span><span class="vo-val">{{ selectedOrder.shipping_status || '—' }}</span></div>
          <div class="vo-row"><span class="vo-key">Shipping Date</span><span class="vo-val">{{ formatDate(selectedOrder.shipping_date) }}</span></div>
        </div>

        <div class="vo-section">
          <div class="vo-row"><span class="vo-key">Comments</span><span class="vo-val">{{ selectedOrder.comments || selectedOrder.notes || '' }}</span></div>
        </div>

        <div class="vo-section vo-proof">
          <div class="vo-proof__head">
            <span class="vo-key">Payment Proof</span>
            <span v-if="selectedOrderScreenshots.length" class="vo-proof__count">{{ selectedOrderScreenshots.length }} attached</span>
          </div>

          <div v-if="selectedOrderScreenshots.length" class="vo-proof__gallery">
            <div
              v-for="(file, fIdx) in selectedOrderScreenshots"
              :key="file.key || file.url || fIdx"
              class="vo-proof__cell"
              :class="{ 'vo-proof__cell--new': fIdx >= savedProofCount }"
            >
              <button
                type="button"
                class="vo-proof__thumb"
                :title="file.name || 'Payment screenshot'"
                @click="openProofPreview(fIdx)"
              >
                <img
                  v-if="!isPdfAttachment(file)"
                  :src="file.url"
                  :alt="file.name || 'Payment screenshot'"
                  class="vo-proof__img"
                  loading="lazy"
                />
                <span v-else class="vo-proof__pdf">
                  <span class="vo-proof__pdf-badge">PDF</span>
                  <span class="vo-proof__pdf-name">{{ file.name || 'Document' }}</span>
                </span>
              </button>
              <span v-if="fIdx >= savedProofCount" class="vo-proof__new-tag">New</span>
              <button
                v-if="fIdx >= savedProofCount && canAttachPaymentProof"
                type="button"
                class="vo-proof__remove"
                :aria-label="`Remove ${file.name || 'screenshot'}`"
                :disabled="proofSaving"
                @click="removeNewProofScreenshot(fIdx - savedProofCount)"
              >×</button>
              <button
                v-if="fIdx < savedProofCount && canDeleteSavedProof"
                type="button"
                class="vo-proof__remove"
                :aria-label="`Delete ${file.name || 'screenshot'}`"
                :disabled="proofDeleting"
                @click="askDeleteSavedProof(file)"
              >×</button>
            </div>
          </div>

          <p v-if="selectedOrder.payment_proof_note" class="vo-proof__note">
            <span class="vo-proof__note-label">Note</span>
            {{ selectedOrder.payment_proof_note }}
          </p>

          <p v-if="!selectedOrderScreenshots.length && !selectedOrder.payment_proof_note" class="vo-proof__empty">
            No payment proof was attached for this order yet.
          </p>

          <div v-if="canAttachPaymentProof" class="vo-proof__actions">
            <input
              ref="proofFileInput"
              type="file"
              class="vo-proof__file-input"
              accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
              multiple
              @change="onProofFilesSelected"
            />
            <button
              type="button"
              class="vo-proof__attach-btn"
              :disabled="proofUploading > 0 || proofSaving"
              @click="triggerProofUpload"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span>{{ proofUploading > 0 ? `Uploading ${proofUploading}…` : 'Attach screenshot' }}</span>
            </button>
            <button
              v-if="proofNewScreenshots.length"
              type="button"
              class="vo-proof__save-btn"
              :disabled="proofSaving || proofUploading > 0"
              @click="saveProofScreenshots"
            >
              {{ proofSaving ? 'Saving…' : `Save ${proofNewScreenshots.length} screenshot${proofNewScreenshots.length > 1 ? 's' : ''}` }}
            </button>
          </div>

          <p v-if="proofError" class="vo-proof__error">{{ proofError }}</p>
        </div>
      </div>

      <template #footer>
        <button
          v-if="selectedOrder && canShareTracking(selectedOrder)"
          type="button"
          class="vo-whatsapp-btn"
          :class="{ 'vo-whatsapp-btn--sent': selectedOrder.tracking_notified_at }"
          :title="selectedOrder.tracking_notified_at ? `Already sent ${formatDateTime(selectedOrder.tracking_notified_at)} — click to resend` : ''"
          @click="sendTrackingViaWhatsapp(selectedOrder)"
        >
          <svg v-if="!selectedOrder.tracking_notified_at" viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479c0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.886 9.884zm8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
          </svg>
          <svg v-else viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          <span>{{ selectedOrder.tracking_notified_at ? 'Sent — Resend' : 'Send Tracking' }}</span>
        </button>
        <CButton color="secondary" variant="outline" @click="showDetailsModal = false">Close</CButton>
        <CButton v-if="canEditOrders" color="primary" @click="editSelectedOrder">Edit</CButton>
      </template>
    </CModal>

    <CModal
      :show.sync="showHistoryModal"
      size="lg"
      :centered="true"
      title="Order History"
      class="view-order-modal history-modal"
    >
      <div class="hist-head">
        <div class="hist-head__who">
          <span class="hist-head__label">Customer</span>
          <span class="hist-head__name">{{ historyCustomer.name }}</span>
          <span v-if="historyCustomer.phone" class="hist-head__phone">{{ formatPhone(historyCustomer.phone) }}</span>
        </div>
      </div>

      <div class="hist-stats">
        <div class="hist-stat">
          <span class="hist-stat__label">Calls</span>
          <span class="hist-stat__value">{{ historyCalls.length }}</span>
        </div>
        <div class="hist-stat">
          <span class="hist-stat__label">Orders</span>
          <span class="hist-stat__value">{{ historyOrders.length }}</span>
        </div>
        <div class="hist-stat">
          <span class="hist-stat__label">Lifetime</span>
          <span class="hist-stat__value">{{ currency(historyLifetime) }}</span>
        </div>
        <div class="hist-stat">
          <span class="hist-stat__label">Last Call</span>
          <span class="hist-stat__value hist-stat__value--small">{{ historyLastCall }}</span>
        </div>
      </div>

      <div v-if="!historyOrders.length" class="hist-state">No orders found for this customer.</div>

      <div v-else class="hist-list">
        <div v-for="horder in historyOrders" :key="`hist-${horder.id}`" class="hist-card">
          <div class="hist-card__head">
            <div class="hist-card__id">
              <span class="hist-card__no">{{ horder.order_number || '—' }}</span>
              <span v-if="horder.invoice_number" class="hist-card__inv">{{ horder.invoice_number }}</span>
            </div>
            <span class="hist-card__amount">{{ currency(horder.grand_total) }}</span>
          </div>

          <div class="hist-card__badges">
            <span :class="['status-pill', displayOrderStatusClass(horder)]">{{ displayOrderStatus(horder) }}</span>
            <span :class="['status-pill', paymentBadgeClass(horder.payment_status)]">{{ paymentStatusLabel(horder.payment_status) }}</span>
            <span
              v-if="historyRefundBadge(horder)"
              :class="['status-pill', historyRefundBadge(horder).cls]"
            >{{ historyRefundBadge(horder).label }}</span>
          </div>

          <div class="hist-card__items" :title="historyItemsSummary(horder)">{{ historyItemsSummary(horder) }}</div>

          <div class="hist-card__foot">
            <span class="hist-card__date">{{ orderDateDisplay(horder) }}</span>
            <button type="button" class="hist-view-btn" @click="openHistoryOrderDetail(horder)">View</button>
          </div>
        </div>
      </div>

      <template #footer>
        <CButton color="secondary" variant="outline" @click="showHistoryModal = false">Close</CButton>
      </template>
    </CModal>

    <transition name="vo-proof-fade">
      <div v-if="showProofPreview && proofPreviewFile" class="vo-proof-preview" role="dialog" aria-modal="true" @click.self="closeProofPreview">
        <div class="vo-proof-preview__dialog">
          <header class="vo-proof-preview__bar">
            <span class="vo-proof-preview__title" :title="proofPreviewFile.name || 'Payment screenshot'">{{ proofPreviewFile.name || 'Payment screenshot' }}</span>
            <div class="vo-proof-preview__bar-actions">
              <a :href="proofPreviewFile.url" target="_blank" rel="noopener" class="vo-proof-preview__icon-btn" title="Open in new tab" aria-label="Open in new tab">
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </a>
              <button type="button" class="vo-proof-preview__icon-btn" aria-label="Close preview" @click="closeProofPreview">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </header>
          <div class="vo-proof-preview__stage">
            <button v-if="selectedOrderScreenshots.length > 1" type="button" class="vo-proof-preview__nav vo-proof-preview__nav--prev" aria-label="Previous" @click="stepProofPreview(-1)">‹</button>
            <img v-if="!isPdfAttachment(proofPreviewFile)" :src="proofPreviewFile.url" :alt="proofPreviewFile.name || 'Payment screenshot'" class="vo-proof-preview__img" />
            <iframe v-else :src="proofPreviewFile.url" class="vo-proof-preview__pdf" title="PDF preview"></iframe>
            <button v-if="selectedOrderScreenshots.length > 1" type="button" class="vo-proof-preview__nav vo-proof-preview__nav--next" aria-label="Next" @click="stepProofPreview(1)">›</button>
          </div>
          <footer v-if="selectedOrderScreenshots.length > 1" class="vo-proof-preview__count">{{ proofPreviewIndex + 1 }} / {{ selectedOrderScreenshots.length }}</footer>
        </div>
      </div>
    </transition>

    <CModal
      :show.sync="showEditModal"
      size="lg"
      :centered="true"
      :title="editOrderTitle"
      class="edit-order-modal"
      :close-on-backdrop="!isSavingEdit"
    >
      <div v-if="editOrder" class="qe-body">
        <div v-if="editError" class="qe-alert">{{ editError }}</div>

        <div class="qe-meta">
          <div class="qe-meta__row">
            <span class="qe-meta__label">Customer</span>
            <span class="qe-meta__value">{{ editOrder.contact_name || '—' }} <span v-if="editOrder.contact_phone" class="qe-meta__dim">· {{ editOrder.contact_phone }}</span></span>
          </div>
          <div class="qe-meta__row">
            <span class="qe-meta__label">Total</span>
            <span class="qe-meta__value">{{ currency(editOrder.grand_total) }} <span class="qe-meta__dim">· {{ totalBottlesFor(editOrder) }} item(s)</span></span>
          </div>
          <div class="qe-meta__row">
            <span class="qe-meta__label">Placed</span>
            <span class="qe-meta__value">{{ formatDateTime(editOrder.created_at) }}</span>
          </div>
        </div>

        <div class="qe-section">
          <h3 class="qe-section__title">Order</h3>
          <div class="qe-grid">
            <div class="qe-field">
              <label class="orders-label">Order Status</label>
              <AppDropdown
                v-model="editForm.order_status"
                :options="editOrderStatusOptions"
                placeholder="Select status…"
              />
            </div>
            <div class="qe-field">
              <label class="orders-label">Care Of</label>
              <AppDropdown
                v-model="editForm.care_of"
                :options="editCareOfOptions"
                searchable
                search-placeholder="Search user…"
                placeholder="No telesales credit"
              />
            </div>
          </div>
        </div>

        <div class="qe-section">
          <h3 class="qe-section__title">Payment</h3>
          <div class="qe-grid qe-grid--3">
            <div class="qe-field">
              <label class="orders-label">Payment Status</label>
              <AppDropdown
                v-model="editForm.payment_status"
                :options="editPaymentStatusOptions"
                placeholder="Select status…"
              />
            </div>
            <div class="qe-field">
              <label class="orders-label">Payment Method</label>
              <AppDropdown
                v-model="editForm.payment_method"
                :options="editPaymentMethodOptions"
                placeholder="Select method…"
              />
            </div>
            <div class="qe-field">
              <label class="orders-label">Payment Date</label>
              <input v-model="editForm.payment_date" type="date" class="orders-input qe-date" />
            </div>
          </div>
        </div>

        <div class="qe-section">
          <h3 class="qe-section__title">Shipping</h3>
          <div class="qe-grid qe-grid--3">
            <div class="qe-field">
              <label class="orders-label">Shipping Status</label>
              <AppDropdown
                v-model="editForm.shipping_status"
                :options="editShippingStatusOptions"
                placeholder="Select status…"
              />
            </div>
            <div class="qe-field">
              <label class="orders-label">Shipping Method</label>
              <AppDropdown
                v-model="editForm.shipping_method"
                :options="editShippingMethodOptions"
                placeholder="Select method…"
              />
            </div>
            <div class="qe-field">
              <label class="orders-label">Shipping Date</label>
              <input v-model="editForm.shipping_date" type="date" class="orders-input qe-date" />
            </div>
            <div class="qe-field qe-field--wide">
              <label class="orders-label">Tracking ID</label>
              <input
                v-model.trim="editForm.tracking_id"
                type="text"
                class="orders-input"
                placeholder="Courier tracking number"
              />
            </div>
          </div>
        </div>

        <div class="qe-section">
          <h3 class="qe-section__title">Notes</h3>
          <textarea
            v-model="editForm.notes"
            class="orders-input qe-textarea"
            rows="3"
            placeholder="Internal notes…"
          ></textarea>
        </div>
      </div>

      <template #footer>
        <button type="button" class="qe-full-edit" :disabled="isSavingEdit" @click="goToFullEdit">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
          <span>Edit address &amp; items…</span>
        </button>
        <span class="qe-footer-spacer" aria-hidden="true"></span>
        <CButton color="secondary" variant="outline" :disabled="isSavingEdit" @click="closeEditModal">Cancel</CButton>
        <CButton color="primary" :disabled="isSavingEdit" @click="submitEdit">
          {{ isSavingEdit ? 'Saving…' : 'Save Changes' }}
        </CButton>
      </template>
    </CModal>

    <AppConfirmDialog
      :visible="showCancelConfirm"
      title="Cancel this order?"
      :message="cancelConfirmMessage"
      confirm-label="Cancel Order"
      cancel-label="Keep Order"
      busy-label="Cancelling…"
      :busy="isCancelling"
      variant="danger"
      :close-on-backdrop="!isCancelling"
      @confirm="confirmCancelOrder"
      @cancel="closeCancelConfirm"
    />

    <AppConfirmDialog
      :visible="proofDelete.visible"
      title="Delete screenshot?"
      :message="proofDelete.message"
      confirm-label="Delete"
      cancel-label="Cancel"
      busy-label="Deleting…"
      :busy="proofDeleting"
      variant="danger"
      :close-on-backdrop="!proofDeleting"
      @confirm="confirmDeleteSavedProof"
      @cancel="proofDelete.visible = false"
    />

    <CModal
      :show.sync="showRefundModal"
      size="lg"
      :centered="true"
      :title="refundModalTitle"
      class="refund-modal"
      :close-on-backdrop="!isSavingRefund"
    >
      <div v-if="refundOrder" class="rf-body">
        <div v-if="refundError" class="qe-alert">{{ refundError }}</div>

        <div class="qe-meta">
          <div class="qe-meta__row">
            <span class="qe-meta__label">Order</span>
            <span class="qe-meta__value">{{ refundOrder.order_number || `#${refundOrder.id}` }}</span>
          </div>
          <div class="qe-meta__row">
            <span class="qe-meta__label">Customer</span>
            <span class="qe-meta__value">{{ refundOrder.contact_name || '—' }} <span v-if="refundOrder.contact_phone" class="qe-meta__dim">· {{ refundOrder.contact_phone }}</span></span>
          </div>
          <div class="qe-meta__row">
            <span class="qe-meta__label">Order Total</span>
            <span class="qe-meta__value">{{ currency(refundOrder.grand_total) }}</span>
          </div>
        </div>

        <div class="qe-section">
          <h3 class="qe-section__title">Refund Details</h3>
          <div class="qe-grid">
            <div class="qe-field">
              <label class="orders-label">Account Holder Name <span class="rf-req">*</span></label>
              <input v-model.trim="refundForm.name" type="text" class="orders-input" placeholder="Name on bank account" />
            </div>
            <div class="qe-field">
              <label class="orders-label">Refund Amount (₹) <span class="rf-req">*</span></label>
              <input v-model.number="refundForm.amount" type="number" min="1" :max="Number(refundOrder.grand_total || 0)" step="0.01" class="orders-input" />
            </div>
            <div class="qe-field">
              <label class="orders-label">Bank Account Number <span class="rf-req">*</span></label>
              <input v-model.trim="refundForm.bank_account_no" type="text" class="orders-input" placeholder="Account number" />
            </div>
            <div class="qe-field">
              <label class="orders-label">IFSC Code <span class="rf-req">*</span></label>
              <input v-model.trim="refundForm.IFSC" type="text" class="orders-input" placeholder="e.g. SBIN0001234" maxlength="11" @input="refundForm.IFSC = refundForm.IFSC.toUpperCase()" />
            </div>
            <div class="qe-field qe-field--wide">
              <label class="orders-label">Reason <span class="rf-req">*</span></label>
              <textarea v-model="refundForm.reason" class="orders-input qe-textarea" rows="3" placeholder="Why is this refund being issued?"></textarea>
            </div>
            <div class="qe-field qe-field--wide">
              <label class="orders-label">Proof (optional)</label>
              <div
                class="rf-dropzone"
                :class="{
                  'is-dragover': isRefundDragging,
                  'has-file': !!refundForm.proofFile,
                  'is-disabled': isSavingRefund
                }"
                @click="openRefundProofPicker"
                @dragover.prevent="onRefundProofDragOver"
                @dragleave.prevent="onRefundProofDragLeave"
                @drop.prevent="onRefundProofDrop"
              >
                <input
                  ref="refundProofInput"
                  type="file"
                  accept="image/*,application/pdf"
                  class="rf-dropzone__input"
                  :disabled="isSavingRefund"
                  @change="onRefundProofSelected"
                />

                <template v-if="refundForm.proofFile">
                  <div class="rf-dropzone__file-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                  </div>
                  <div class="rf-dropzone__file-meta">
                    <span class="rf-dropzone__file-name">{{ refundForm.proofFile.name }}</span>
                    <span class="rf-dropzone__file-size">{{ refundProofSizeLabel }} · click or drop to replace</span>
                  </div>
                  <button type="button" class="rf-dropzone__remove" :disabled="isSavingRefund" @click.stop="clearRefundProof">Remove</button>
                </template>

                <template v-else>
                  <div class="rf-dropzone__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                  </div>
                  <div class="rf-dropzone__prompt">
                    <span class="rf-dropzone__title">Drop proof or click to upload</span>
                    <span class="rf-dropzone__hint">PNG, JPG or PDF · uploaded to Cloudflare R2</span>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <span class="qe-footer-spacer" aria-hidden="true"></span>
        <CButton color="secondary" variant="outline" :disabled="isSavingRefund" @click="closeRefundModal">Cancel</CButton>
        <CButton color="warning" :disabled="isSavingRefund || !isRefundFormValid" @click="submitRefund">
          {{ isSavingRefund ? 'Submitting…' : 'Submit Refund Request' }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import DateRangePicker from '@/components/DateRangePicker.vue'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'
import { ORGANISATION_ID_TO_NAME } from '@/views/users/userHelpers'
import { lineDiscountBreakdown } from '@/utils/discountPresets.js'

// Cross-org search is scoped to these org IDs server-side (see
// orderController::searchOrder) — org 3 (MCCI Patients C/O) is excluded,
// so the Organisation filter only offers the orgs search can actually reach.
const SEARCHABLE_ORG_IDS = [1, 2, 4]

const FULL_TABS = [
  { key: 'search', label: 'Search Orders', mode: 'search', selectable: true },
  { key: 'all', label: 'All Orders', endpoint: '/api/orders', mode: 'get', paginated: true, selectable: true, allOrgs: true },
  { key: 'org', label: 'Org Orders', endpoint: '/api/orders', mode: 'get', paginated: true, selectable: true },
  { key: 'bulk', label: 'Bulk Orders', endpoint: '/api/orders', mode: 'get', paginated: true, selectable: true, discountedOnly: true },
  { key: 'care_of', label: 'C/O Orders', endpoint: '/api/orders', mode: 'get', paginated: true, selectable: true, careOfOnly: true },
  { key: 'tracking_pending', label: 'Pending Tracking', endpoint: '/api/orders', mode: 'get', paginated: true, selectable: true, trackingPending: true },
  { key: 'shipping_pending', label: 'Shipping Pending', endpoint: '/api/shipping_pending', mode: 'get', paginated: true, selectable: true },
  { key: 'manifest_failed', label: 'Manifest Failed', endpoint: '/api/manifest_failed', mode: 'get', paginated: true, selectable: true },
  { key: 'on_hold', label: 'On Hold Orders', endpoint: '/api/confirmation_pending', mode: 'get', paginated: true, selectable: true },
  { key: 'my_orders', label: 'My Orders', endpoint: '/api/myorders', mode: 'get', paginated: true, selectable: true },
  { key: 'my_returned', label: 'My Returned Orders', endpoint: '/api/myreturnedorders', mode: 'post', paginated: true, needsDateRange: true, selectable: false },
  { key: 'returned', label: 'Returned Orders', endpoint: '/api/returnedorders', mode: 'post', paginated: false, needsDateRange: true, supportsExport: true, selectable: false },
  { key: 'cancelled', label: 'Cancelled Orders', endpoint: '/api/cancelledorders', mode: 'post', paginated: false, needsDateRange: true, supportsExport: true, selectable: false }
]

const FIELD_STAFF_TABS = [
  { key: 'search', label: 'Search Orders', mode: 'search', selectable: true },
  { key: 'all', label: 'All Orders', endpoint: '/api/myorders', mode: 'get', paginated: true, selectable: true }
]

const TELESALES_TABS = [
  { key: 'search', label: 'Search Orders', mode: 'search', selectable: true },
  { key: 'all', label: 'My All Orders', endpoint: '/api/myorders', mode: 'get', paginated: true, selectable: true },
  { key: 'pending_invoices', label: 'Pending Invoices', endpoint: '/api/my_pending_invoices', mode: 'get', paginated: true, selectable: false, pendingInvoice: true },
  { key: 'my_care_of', label: 'My C/O Orders', endpoint: '/api/my_care_of_orders', mode: 'get', paginated: true, selectable: true },
  { key: 'my_tracking_pending', label: 'My Pending Tracking', endpoint: '/api/myorders', mode: 'get', paginated: true, selectable: true, trackingPending: true, myOnly: true },
  { key: 'my_shipping_pending', label: 'My Shipping Pending', endpoint: '/api/my_shipping_pending', mode: 'get', paginated: true, selectable: true },
  { key: 'my_returned', label: 'My Returned Orders', endpoint: '/api/myreturnedorders', mode: 'post', paginated: true, needsDateRange: true, selectable: false },
  { key: 'my_cancelled', label: 'My Cancelled Orders', endpoint: '/api/mycancelledorders', mode: 'post', paginated: false, needsDateRange: true, supportsExport: true, selectable: false }
]

// Counter billers don't dispatch — they sell at the POS. Shipping /
// tracking tabs would always be empty, so we surface only the tabs that
// match a counter workflow: search, my orders, my returns, my cancels.
// The backend already scopes `/api/myorders*` and `/api/orders/search_orders`
// to the caller's user_id when their role is `biller`
// (orderController::searchOrder line 3884 + myOrders line 2583).
const BILLER_TABS = [
  { key: 'search', label: 'Search Orders', mode: 'search', selectable: true },
  { key: 'all', label: 'My All Orders', endpoint: '/api/myorders', mode: 'get', paginated: true, selectable: true },
  { key: 'my_returned', label: 'My Returned Orders', endpoint: '/api/myreturnedorders', mode: 'post', paginated: true, needsDateRange: true, selectable: false },
  { key: 'my_cancelled', label: 'My Cancelled Orders', endpoint: '/api/mycancelledorders', mode: 'post', paginated: false, needsDateRange: true, supportsExport: true, selectable: false }
]

const ADMIN_TIER_ROLES = ['superadmin', 'admin', 'manager', 'operations', 'finance', 'ecom_manager', 'sales_supervisor']

// Roles permitted to use the Quick Edit modal. Mirrors the role gate on
// the `quickUpdate` backend endpoint — keep the two in sync if either is
// widened, otherwise the UI button will hit a 403.
const ORDER_EDIT_ROLES = ['superadmin', 'admin', 'manager', 'accountant', 'finance_manager', 'senior_accountant_2', 'hr_manager']

// Cancel / refund is a stricter tier than edit: hr_manager may edit orders
// (quick + full edit) but must NOT cancel or raise refunds, so it's excluded
// here. Mirrors the backend destroy() gate — keep the two in sync.
const ORDER_CANCEL_ROLES = ['superadmin', 'admin', 'manager', 'accountant', 'finance_manager', 'senior_accountant_2']

const QUICK_EDIT_FIELDS = [
  'order_status',
  'payment_status', 'payment_method', 'payment_date',
  'shipping_status', 'shipping_method', 'shipping_date',
  'tracking_id',
  'care_of',
  'notes'
]

function blankEditForm () {
  return {
    order_status: '',
    payment_status: '',
    payment_method: '',
    payment_date: '',
    shipping_status: '',
    shipping_method: '',
    shipping_date: '',
    tracking_id: '',
    care_of: 'none',
    notes: ''
  }
}

function blankRefundForm () {
  return {
    name: '',
    amount: 0,
    bank_account_no: '',
    IFSC: '',
    reason: '',
    proofFile: null
  }
}

export default {
  name: 'Orders',
  components: { AppDropdown, DateRangePicker, AppConfirmDialog },
  data () {
    return {
      activeTab: 'all',
      // Curated quick-select presets for the Search Orders date picker. Reuses
      // DateRangePicker's built-in preset keys (rangeForKey) — only the three
      // ranges the sales team asks for most, instead of the full default list.
      searchDatePresets: [
        { key: 'today', label: 'Today' },
        { key: 'yesterday', label: 'Yesterday' },
        { key: 'mtd', label: 'This Month' },
        { key: 'last-month', label: 'Last Month' }
      ],
      // Horizontal-scroll affordances for the tab strip — arrows show only
      // when there's overflow in that direction (see updateTabScroll).
      canScrollLeft: false,
      canScrollRight: false,
      orders: [],
      // Keyed by order id → full order object so a selection survives a
      // re-search (the search tab swaps `orders` out on every query) and so
      // bulk label printing still has the row data for orders no longer in
      // the visible result set.
      selectedOrdersById: {},
      selectedOrder: null,
      showDetailsModal: false,
      // Customer order-history modal (per-row "History" action). Ungated —
      // available to every role; the backend scopes results to the user's org.
      showHistoryModal: false,
      historyOrders: [],
      // Call history for the same customer (from searchCropsByPhone `calls`) —
      // powers the Calls / Last Call summary cards, mirroring TeleSales.
      historyCalls: [],
      // Order id whose History button is mid-fetch — drives the inline button
      // spinner and blocks re-entry until the modal opens.
      historyLoadingId: null,
      historyCustomer: { name: '', phone: '' },
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      // Order id whose "Copy for Accounts" button is flashing its copied
      // state — reset by a short timer so the tick doesn't linger.
      copiedOrderId: null,
      // Discount tiers fetched lazily per organisation (the list can span
      // orgs) and cached by org id, so the "Copy for Accounts" breakdown can
      // reconstruct each order's per-product discount without refetching.
      discountPresetsByOrg: {},
      // Order id whose invoice PDF is currently being fetched — guards against
      // double-clicks and drives the Invoice button's loading state.
      downloadingInvoiceId: null,
      activePage: 1,
      maxPages: 1,
      users: [],
      orderTypes: [],
      products: [],
      searchFieldOptions: [
        { label: 'Order Number', value: 'order_number' },
        { label: 'Invoice Number', value: 'invoice_number' },
        { label: 'Website Order ID', value: 'website_order_id' },
        { label: 'Tracking ID', value: 'tracking_id' },
        { label: 'Customer Name', value: 'contact_name' },
        { label: 'Customer Phone', value: 'contact_phone' },
        { label: 'Customer Address', value: 'contact_address' },
        { label: 'Customer Post Office', value: 'contact_postofficename' },
        { label: 'Customer District', value: 'contact_district' },
        { label: 'Customer State', value: 'contact_state' },
        { label: 'Customer Pincode', value: 'contact_pincode' },
        { label: 'No.of Bottles', value: 'item_count' },
        { label: 'Shipping Status', value: 'shipping_status' },
        { label: 'Marketplace Order ID', value: 'marketplace_order_id' }
      ],
      searchForm: {
        search_parameter: 'order_number',
        search_value: '',
        start_date: '',
        end_date: '',
        minBottles: 1,
        maxBottles: 1000,
        organisation: 'all',
        user: 'any',
        care_of: 'any',
        product: '',
        order_type: 'any',
        payment_method: '',
        order_status: '',
        shipping_status: '',
        payment_status: '',
        state_name: 'all',
        district_name: 'all',
        local_body_name: 'all'
      },
      dateFilter: {
        start_date: '',
        end_date: ''
      },
      careOfFilter: {
        care_of_user: 'any',
        start_date: '',
        end_date: ''
      },
      // Quick Edit modal — operational fields only (status, payment,
      // shipping, tracking, care_of, notes). Avoids navigating away from
      // the listing for routine edits.
      showEditModal: false,
      editOrder: null,
      editForm: blankEditForm(),
      isSavingEdit: false,
      editError: '',
      dropdownGroups: {
        order_status: [],
        payment_status: [],
        payment_method: [],
        shipping_status: [],
        shipping_method: []
      },
      returnSummary: {
        flipkart_returned: 0,
        amazon_returned: 0,
        ip_returned: 0,
        grand_total_amount: 0
      },
      trackingStats: {
        pending: 0,
        sent_today: 0,
        sent_this_week: 0
      },
      showCancelConfirm: false,
      orderToCancel: null,
      isCancelling: false,
      showRefundModal: false,
      refundOrder: null,
      refundForm: blankRefundForm(),
      isSavingRefund: false,
      refundError: '',
      isRefundDragging: false,
      // Lightbox state for the View-Order modal's payment-proof gallery.
      // Indexes into selectedOrderScreenshots.
      showProofPreview: false,
      proofPreviewIndex: 0,
      // "Attach more screenshots" flow inside the View modal. proofNewScreenshots
      // holds uploaded-but-not-yet-saved attachments; they're committed (appended
      // to the order) by saveProofScreenshots.
      proofNewScreenshots: [],
      proofUploading: 0,
      proofSaving: false,
      proofError: '',
      // Delete flow for SAVED screenshots (superadmin only) — mirrors the
      // Inbox Payment Approvals delete against the same backend endpoint.
      proofDelete: { visible: false, shot: null, message: '' },
      proofDeleting: false
    }
  },
  computed: {
    // Payment-proof attachments already saved on the open order. The column is
    // cast to array on the backend, but a legacy order may arrive with null
    // or a stray non-array — guard so the gallery never throws.
    savedProofScreenshots () {
      const raw = this.selectedOrder && this.selectedOrder.payment_screenshots
      if (!Array.isArray(raw)) return []
      return raw.filter(f => f && f.url)
    },
    savedProofCount () {
      return this.savedProofScreenshots.length
    },
    // Saved attachments followed by the not-yet-committed uploads, so the
    // gallery and lightbox treat them as one continuous list.
    selectedOrderScreenshots () {
      return [...this.savedProofScreenshots, ...this.proofNewScreenshots]
    },
    proofPreviewFile () {
      return this.selectedOrderScreenshots[this.proofPreviewIndex] || null
    },
    // Per-line discount split for every order on screen — the list rows plus
    // the order open in the detail modal — keyed by order id so the item
    // cells are a map lookup instead of a re-resolve per line. Recomputes
    // when the preset cache fills, so the tags appear as soon as the tiers
    // land (see ensureDiscountPresets).
    lineDiscountsByOrderId () {
      const map = {}
      const rows = Array.isArray(this.orders) ? this.orders.slice() : []
      if (this.selectedOrder) rows.push(this.selectedOrder)
      rows.forEach((order) => {
        if (!order || order.id == null || map[order.id]) return
        const split = this.resolveLineDiscounts(order)
        if (split) map[order.id] = split
      })
      return map
    },
    // The discount total row needs a real rupee figure to print. `hasDiscount`
    // is deliberately looser (it also fires on discount_percentage, which the
    // list chip uses), so gate the row on the amount itself — otherwise an
    // order carrying only a percentage renders a "0% / −₹0" total.
    hasOrderDiscountAmount () {
      return Boolean(this.selectedOrder && Number(this.selectedOrder.discount) > 0)
    },
    // Products subtotal net of the order discount — what the item rows sum
    // to once each line's share is taken off. Excludes shipping / COD, so
    // it matches the Total column rather than grand_total.
    afterDiscountTotal () {
      if (!this.selectedOrder) return '—'
      const subtotal = Number(this.selectedOrder.total || 0)
      const discount = Number(this.selectedOrder.discount || 0)
      if (!Number.isFinite(subtotal) || !Number.isFinite(discount)) return '—'
      return Math.round((subtotal - discount) * 100) / 100
    },
    // Only give the detail table a Discount column when at least one line of
    // the open order actually earned something.
    hasLineDiscounts () {
      if (!this.selectedOrder) return false
      const split = this.lineDiscountsByOrderId[this.selectedOrder.id]
      return Boolean(split && split.some((line) => Number(line.percent) > 0))
    },
    // Who may attach proof: the order-edit roles, plus sales_admin and the
    // order-creating roles (telesales / biller / field staff) who routinely
    // add the bank transfer screenshot after placing the order. Backend
    // enforces the same.
    canAttachPaymentProof () {
      if (this.canEditOrders) return true
      const roles = this.getStoredRoles().map(r => String(r).toLowerCase())
      return roles.some(r => ['sales_admin', 'telesales', 'biller', 'field_staff'].includes(r))
    },
    // Deleting a SAVED screenshot destroys payment evidence, so it's held to
    // a stricter tier than attaching: superadmin and senior_accountant_2 only
    // on this page. (The backend endpoint is shared with Inbox → Payment
    // Approvals and allows more roles there — this is a deliberate UI-level
    // restriction.)
    canDeleteSavedProof () {
      const roles = this.getStoredRoles().map(r => String(r).toLowerCase())
      return roles.some(r => ['superadmin', 'senior_accountant_2'].includes(r))
    },
    visibleTabs () {
      if (this.isFieldStaff) return FIELD_STAFF_TABS
      if (this.isTelesalesStaff) return TELESALES_TABS
      // Billers see only their own POS bills — same shape as the
      // telesales view, but trimmed of shipping/tracking tabs that
      // never apply to a counter workflow.
      if (this.isBillerStaff) return BILLER_TABS
      // Hide cross-org tabs from anyone without switch_org permission. The
      // backend ignores `all_orgs=1` for these users anyway, so showing the
      // tab would just mislead them into seeing identical data to "Org Orders".
      if (!this.canSwitchOrg) {
        return FULL_TABS.filter(tab => !tab.allOrgs)
      }
      return FULL_TABS
    },
    canSwitchOrg () {
      return Boolean(this.$store && this.$store.getters && this.$store.getters.can && this.$store.getters.can('switch_org'))
    },
    activeTabConfig () {
      return this.visibleTabs.find(tab => tab.key === this.activeTab) || this.visibleTabs[0]
    },
    isPendingInvoiceTab () {
      return Boolean(this.activeTabConfig && this.activeTabConfig.pendingInvoice)
    },
    isFieldStaff () {
      return this.getStoredRoles().includes('field_staff')
    },
    isTelesalesStaff () {
      const roles = this.getStoredRoles().map(r => String(r).toLowerCase())
      if (!roles.includes('telesales')) return false
      return !roles.some(r => ADMIN_TIER_ROLES.includes(r))
    },
    isBillerStaff () {
      // Mirrors the telesales gate: a user is a "pure biller" only if
      // they hold the biller role and don't ALSO sit in an admin-tier
      // role (admin / superadmin / manager / etc.). Without this guard
      // an admin who's been given biller as a secondary role would lose
      // access to FULL_TABS and only see their own bills.
      const roles = this.getStoredRoles().map(r => String(r).toLowerCase())
      if (!roles.includes('biller')) return false
      return !roles.some(r => ADMIN_TIER_ROLES.includes(r))
    },
    showDateFilter () {
      return Boolean(this.activeTabConfig && this.activeTabConfig.needsDateRange)
    },
    showReturnedSummary () {
      return this.activeTab === 'returned'
    },
    // Realised lifetime spend — sums live orders only. Cancelled orders now
    // appear in the table (the fetch sends include_trashed=1) but are excluded
    // here, since a cancelled order is reversed money, not revenue.
    historyLifetime () {
      return this.historyOrders
        .filter(order => !this.isOrderCancelled(order))
        .reduce((sum, order) => sum + Number(order.grand_total || 0), 0)
    },
    // Date of the customer's most recent call. searchCropsByPhone returns calls
    // id-DESC with a pre-formatted `date`, so the first row is the latest.
    historyLastCall () {
      if (!this.historyCalls.length) return '—'
      return this.historyCalls[0].date || '—'
    },
    canExportCurrentTab () {
      return Boolean(this.activeTabConfig && this.activeTabConfig.supportsExport)
    },
    canSelectRows () {
      return Boolean(this.activeTabConfig && this.activeTabConfig.selectable)
    },
    selectedOrderIds () {
      return Object.keys(this.selectedOrdersById).map(id => Number(id))
    },
    selectedOrders () {
      return Object.values(this.selectedOrdersById)
    },
    canBulkPrint () {
      return this.canSelectRows && this.selectedOrderIds.length > 0
    },
    areAllOrdersSelected () {
      return this.canSelectRows && this.orders.length > 0 && this.orders.every(order => this.selectedOrderIds.includes(order.id))
    },
    userDropdownOptions () {
      return [
        { value: 'any', label: 'Any User' },
        ...this.users.map(user => ({ value: String(user.id), label: user.name }))
      ]
    },
    productFilterOptions () {
      return [
        { value: '', label: 'Any Product' },
        ...this.products.map(item => ({
          value: String(item.id),
          label: item.sku ? `${item.name} (${item.sku})` : item.name
        }))
      ]
    },
    searchDateRange: {
      get () {
        return { fromDate: this.searchForm.start_date, toDate: this.searchForm.end_date }
      },
      set (value) {
        this.searchForm.start_date = value && value.fromDate ? value.fromDate : ''
        this.searchForm.end_date = value && value.toDate ? value.toDate : ''
      }
    },
    filterDateRange: {
      get () {
        return { fromDate: this.dateFilter.start_date, toDate: this.dateFilter.end_date }
      },
      set (value) {
        this.dateFilter.start_date = value && value.fromDate ? value.fromDate : ''
        this.dateFilter.end_date = value && value.toDate ? value.toDate : ''
      }
    },
    careOfDateRange: {
      get () {
        return { fromDate: this.careOfFilter.start_date, toDate: this.careOfFilter.end_date }
      },
      set (value) {
        this.careOfFilter.start_date = value && value.fromDate ? value.fromDate : ''
        this.careOfFilter.end_date = value && value.toDate ? value.toDate : ''
      }
    },
    careOfUserOptions () {
      return [
        { value: 'any', label: 'Any C/O User' },
        ...this.users.map(user => ({ value: String(user.id), label: user.name }))
      ]
    },
    showCareOfFilter () {
      return this.activeTab === 'care_of'
    },
    showTrackingStats () {
      return this.activeTab === 'tracking_pending'
    },
    canEditOrders () {
      const roles = this.getStoredRoles().map(r => String(r).toLowerCase())
      return roles.some(r => ORDER_EDIT_ROLES.includes(r))
    },
    canCancelOrders () {
      // Stricter than canEditOrders — hr_manager can edit but not cancel.
      const roles = this.getStoredRoles().map(r => String(r).toLowerCase())
      return roles.some(r => ORDER_CANCEL_ROLES.includes(r))
    },
    canCopyForAccounts () {
      // The "Copy for Accounts" breakdown is available to everyone except
      // hr_manager (edit-only role — no accounts/cancel/refund affordances).
      const roles = this.getStoredRoles().map(r => String(r).toLowerCase())
      return !roles.includes('hr_manager')
    },
    canRefundOrders () {
      // Telesales staff can raise refund requests too, even though they
      // don't have the broader edit/cancel rights of ORDER_CANCEL_ROLES.
      // hr_manager is intentionally excluded (edit-only, no refunds).
      if (this.canCancelOrders) return true
      const roles = this.getStoredRoles().map(r => String(r).toLowerCase())
      return roles.includes('telesales')
    },
    cancelConfirmMessage () {
      const order = this.orderToCancel
      if (!order) return ''
      const ref = order.order_number || `#${order.id}`
      const customer = order.contact_name || 'customer'
      const total = this.currency(order.grand_total)
      return `${ref} for ${customer} (${total}). This marks the order as cancelled; stock and ledger entries will be reversed by the backend.`
    },
    refundModalTitle () {
      if (!this.refundOrder) return 'Issue Refund'
      const ref = this.refundOrder.order_number || `#${this.refundOrder.id}`
      return `Refund — ${ref}`
    },
    isRefundFormValid () {
      const f = this.refundForm
      const amount = Number(f.amount || 0)
      return Boolean(
        f.name && f.name.trim() &&
        amount > 0 &&
        f.bank_account_no && f.bank_account_no.trim() &&
        f.IFSC && f.IFSC.trim() &&
        f.reason && f.reason.trim()
      )
    },
    refundProofSizeLabel () {
      const file = this.refundForm.proofFile
      if (!file) return ''
      const bytes = Number(file.size || 0)
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
    },
    editOrderTitle () {
      if (!this.editOrder) return 'Edit Order'
      return `Edit Order ${this.editOrder.order_number || `#${this.editOrder.id}`}`
    },
    editOrderStatusOptions () {
      return this.dropdownAsOptions(this.dropdownGroups.order_status, 'Select status…')
    },
    editPaymentStatusOptions () {
      return this.dropdownAsOptions(this.dropdownGroups.payment_status, 'Select payment status…')
    },
    editPaymentMethodOptions () {
      return this.dropdownAsOptions(this.dropdownGroups.payment_method, 'Select payment method…')
    },
    editShippingStatusOptions () {
      return this.dropdownAsOptions(this.dropdownGroups.shipping_status, 'Select shipping status…')
    },
    editShippingMethodOptions () {
      return this.dropdownAsOptions(this.dropdownGroups.shipping_method, 'Select shipping method…')
    },
    editCareOfOptions () {
      return [
        { value: 'none', label: 'No telesales credit' },
        ...this.users.map(user => ({ value: String(user.id), label: user.name }))
      ]
    },
    orderTypeDropdownOptions () {
      const options = [
        { value: 'any', label: 'All Types' },
        ...this.orderTypes.map(type => ({ value: type.slug || type.name, label: type.name }))
      ]
      // Marketplace orders (Amazon / Flipkart) are CSV-imported, so they
      // aren't selectable rows in the order_type drop_down_items the order
      // form uses — yet orders carry order_type 'amazon'/'flipkart' and this
      // page even summarises their returns. Ensure both stay filterable here,
      // appending only when the configured list doesn't already include them.
      const seen = new Set(options.map(o => String(o.value).toLowerCase()))
      ;[
        { value: 'amazon', label: 'Amazon' },
        { value: 'flipkart', label: 'Flipkart' }
      ].forEach((type) => {
        if (!seen.has(type.value)) options.push(type)
      })
      return options
    },
    // Search-filter dropdowns reuse the same canonical option sets the Quick
    // Edit modal renders, but lead with an empty "Any …" option so an unset
    // filter sends '' and the backend skips the where-clause entirely.
    paymentMethodFilterOptions () {
      return this.dropdownAsOptions(this.dropdownGroups.payment_method, 'Any Payment Method')
    },
    orderStatusFilterOptions () {
      return this.dropdownAsOptions(this.dropdownGroups.order_status, 'Any Order Status')
    },
    shippingStatusFilterOptions () {
      return this.dropdownAsOptions(this.dropdownGroups.shipping_status, 'Any Shipping Status')
    },
    paymentStatusFilterOptions () {
      return this.dropdownAsOptions(this.dropdownGroups.payment_status, 'Any Payment Status')
    },
    organisationFilterOptions () {
      return [
        { value: 'all', label: 'All Organisations' },
        ...SEARCHABLE_ORG_IDS.map(id => ({ value: id, label: ORGANISATION_ID_TO_NAME[id] }))
      ]
    }
  },
  watch: {
    activePage () {
      if (!this.activeTabConfig || this.activeTab === 'search' || !this.activeTabConfig.paginated) {
        return
      }
      this.fetchCurrentTabData()
    },
    visibleTabs () {
      this.$nextTick(this.updateTabScroll)
    }
  },
  created () {
    this.bootstrapOrdersPage()
  },
  mounted () {
    this.$nextTick(() => {
      this.scrollActiveTabIntoView()
      this.updateTabScroll()
    })
    window.addEventListener('resize', this.updateTabScroll)
    window.addEventListener('keydown', this.handleProofPreviewKeydown)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.updateTabScroll)
    window.removeEventListener('keydown', this.handleProofPreviewKeydown)
  },
  methods: {
    updateTabScroll () {
      const el = this.$refs.tabBar
      if (!el) return
      // 1px slack so sub-pixel rounding doesn't leave a phantom arrow.
      const maxScroll = el.scrollWidth - el.clientWidth
      this.canScrollLeft = el.scrollLeft > 1
      this.canScrollRight = el.scrollLeft < maxScroll - 1
    },
    scrollTabs (direction) {
      const el = this.$refs.tabBar
      if (!el) return
      // Scroll roughly one viewport-width of tabs at a time.
      const amount = Math.max(el.clientWidth * 0.7, 120)
      el.scrollBy({ left: direction * amount, behavior: 'smooth' })
    },
    scrollActiveTabIntoView () {
      const el = this.$refs.tabBar
      if (!el) return
      const active = el.querySelector('.orders-tab.is-active')
      if (active && active.scrollIntoView) {
        active.scrollIntoView({ inline: 'center', block: 'nearest' })
      }
    },
    apiBase () {
      if (typeof window !== 'undefined') {
        const host = window.location.hostname
        if (host === 'localhost' || host === '127.0.0.1') {
          return ''
        }
      }
      return this.$apiAdress
    },
    async bootstrapOrdersPage () {
      this.initializeDates()
      this.ensureActiveTab()
      // Pre-flip the spinner so we don't flash "No orders found." while the
      // filter metadata is being fetched ahead of the first orders call.
      if (this.activeTab !== 'search') {
        this.isLoading = true
      }

      // Warm the active org's discount tiers so the list-row Discount %
      // badges read against the discountable base from first paint (orders
      // are org-scoped, so the active org covers every row). Fire-and-forget
      // — the labels re-render reactively once the cache populates.
      const activeOrgId = this.$store && this.$store.state ? this.$store.state.activeOrganisationId : null
      if (activeOrgId) this.ensureDiscountPresets(activeOrgId)

      try {
        await this.loadSearchFilters()
        if (this.activeTab !== 'search') {
          await this.fetchCurrentTabData()
        }
      } catch (error) {
        // fetchCurrentTabData handles its own errors; this catch covers an
        // unexpected throw that would otherwise leave isLoading stuck true
        // and the page frozen on the spinner.
        this.isLoading = false
        this.errorMessage = this.extractErrorMessage(error, 'Failed to initialise orders page.')
      }
    },
    token () {
      return localStorage.getItem('api_token') || ''
    },
    authParams (extra = {}) {
      const token = this.token()
      if (token) {
        return { ...extra, token }
      }
      return { ...extra }
    },
    getStoredRoles () {
      const rawRoles = localStorage.getItem('roles')
      if (!rawRoles) {
        return []
      }
      return rawRoles
        .split(',')
        .map(role => role.trim())
        .filter(Boolean)
    },
    initializeDates () {
      const today = new Date()
      const searchStart = new Date('2020-08-01')

      // Search tab is user-initiated (requires a search value or filter) so a
      // wide default is harmless. The date-range tabs (Returned, Cancelled,
      // …) auto-fire on tab open, so default them to the last 90 days —
      // pulling six years of returned orders on first visit was the cause
      // of the multi-second wait. Users can still expand the range manually.
      const filterStart = new Date()
      filterStart.setDate(filterStart.getDate() - 90)

      this.searchForm.start_date = this.toDateInput(searchStart)
      this.searchForm.end_date = this.toDateInput(today)
      this.dateFilter.start_date = this.toDateInput(filterStart)
      this.dateFilter.end_date = this.toDateInput(today)
    },
    toDateInput (dateValue) {
      const year = dateValue.getFullYear()
      const month = String(dateValue.getMonth() + 1).padStart(2, '0')
      const day = String(dateValue.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    ensureActiveTab () {
      const rawTab = this.$route.query.tab
      const resolved = this.resolveTabKey(rawTab)
      if (resolved) {
        this.activeTab = resolved
        return
      }

      // No tab in the URL: prefer 'org' (per-org listing) when available,
      // otherwise fall back to 'all' or the first visible tab. We deliberately
      // do NOT push the default into the URL — keeping `/orders` clean.
      const preferred = this.visibleTabs.find(tab => tab.key === 'org')
        || this.visibleTabs.find(tab => tab.key === 'all')
        || this.visibleTabs[0]
      this.activeTab = preferred ? preferred.key : this.activeTab

      if (rawTab) {
        // URL referenced a tab the user can't see (e.g. stale `?tab=all`
        // bookmark after a permission downgrade). Strip it so the sidebar's
        // "All Organisations" indicator can't misrepresent the data scope.
        const nextQuery = { ...this.$route.query }
        delete nextQuery.tab
        this.$router.replace({ path: this.$route.path, query: nextQuery }).catch(() => {})
      }
    },
    resolveTabKey (raw) {
      if (!raw) return ''
      return this.visibleTabs.some(tab => tab.key === raw) ? raw : ''
    },
    async loadSearchFilters () {
      await Promise.all([this.fetchUsers(), this.fetchOrderTypes(), this.fetchStates()])
    },
    async fetchStates () {
      try {
        await axios.get(`${this.apiBase()}/api/address/states`, {
          params: this.authParams()
        })
      } catch (error) {
        // Optional filter metadata endpoint.
      }
    },
    async fetchUsers () {
      try {
        const response = await axios.get(`${this.apiBase()}/api/users`, {
          params: this.authParams()
        })
        this.users = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        this.users = []
      }
    },
    async fetchOrderTypes () {
      try {
        const response = await axios.get(`${this.apiBase()}/api/order_form_data`, {
          params: this.authParams()
        })
        const payload = response.data || {}
        // formData() returns drop_down_items grouped by `type` column; the
        // order-type list lives at drop_down_items.order_type, NOT order_types.
        const dropdown = payload.drop_down_items || {}
        const groupAsArray = (group) => Array.isArray(group)
          ? group
          : (group ? Object.values(group) : [])

        this.orderTypes = groupAsArray(dropdown.order_type)
        // Same response carries the org-scoped item master — reuse it to
        // populate the Product search filter without a second request.
        this.products = Array.isArray(payload.items) ? payload.items : []
        // Cache the rest for the Quick Edit modal so it can render the same
        // canonical option set the OrderForm uses.
        this.dropdownGroups = {
          order_status: groupAsArray(dropdown.order_status),
          payment_status: groupAsArray(dropdown.payment_status),
          payment_method: groupAsArray(dropdown.payment_method),
          shipping_status: groupAsArray(dropdown.shipping_status),
          shipping_method: groupAsArray(dropdown.shipping_method)
        }
      } catch (error) {
        this.orderTypes = []
        this.products = []
      }
    },
    async switchTab (tabKey) {
      if (this.activeTab === tabKey) {
        return
      }

      // The router-view is keep-alive'd per `fullPath`, so changing the
      // ?tab= query remounts this component. Bootstrap then reads the tab
      // from the URL and fetches. Doing both here would double-fetch.
      const nextQuery = { ...this.$route.query, tab: tabKey }
      this.$router.push({ path: this.$route.path, query: nextQuery }).catch(() => {})
    },
    async reloadCurrentTab () {
      if (this.activeTab === 'search') {
        await this.searchOrders()
        return
      }
      await this.fetchCurrentTabData()
    },
    async applyDateFilter () {
      this.activePage = 1
      await this.fetchCurrentTabData()
    },
    async fetchCurrentTabData () {
      const tab = this.activeTabConfig
      if (!tab || !tab.endpoint) {
        return
      }

      this.errorMessage = ''
      this.isLoading = true
      this.clearSelection()

      try {
        let response
        if (tab.mode === 'post') {
          response = await axios.post(`${this.apiBase()}${tab.endpoint}`, this.buildDateRangePayload(false), {
            params: this.authParams(this.tabRequestParams(tab))
          })
        } else {
          response = await axios.get(`${this.apiBase()}${tab.endpoint}`, {
            params: this.authParams(this.tabRequestParams(tab))
          })
        }

        this.consumeOrderResponse(response.data, tab)
      } catch (error) {
        this.orders = []
        this.maxPages = 1
        this.errorMessage = this.extractErrorMessage(error, 'Unable to load orders for this tab.')
      } finally {
        this.isLoading = false
      }

      if (tab.trackingPending) {
        // Refresh the tile counts in parallel with the listing — they
        // share an org scope so the numbers stay coherent with the rows.
        this.fetchTrackingStats()
      }
    },
    async fetchTrackingStats () {
      try {
        // Pass the active org id so the tiles reflect the same scope as
        // the listing — without this, the backend falls back to the
        // user's persisted org, which can lag the in-flight change_my_org
        // POST and show counts for the previous org.
        const params = {}
        const orgId = this.$store && this.$store.state ? this.$store.state.activeOrganisationId : null
        if (orgId) {
          params.organisation_id = orgId
        }
        // "My Pending Tracking" tab scopes both the listing and the tiles
        // to the caller so the counts match what the user actually sees.
        if (this.activeTabConfig && this.activeTabConfig.myOnly) {
          params.my_only = 1
        }
        const response = await axios.get(`${this.apiBase()}/api/orders/tracking_notification_stats`, {
          params: this.authParams(params)
        })
        const data = response.data || {}
        this.trackingStats = {
          pending: Number(data.pending || 0),
          sent_today: Number(data.sent_today || 0),
          sent_this_week: Number(data.sent_this_week || 0)
        }
      } catch (error) {
        // Stats are nice-to-have — don't surface an error if they fail.
      }
    },
    tabRequestParams (tab) {
      const params = { page: this.activePage }
      if (tab && tab.allOrgs) {
        params.all_orgs = 1
      } else if (tab && tab.endpoint === '/api/orders') {
        // Per-org tab: pass the active org id from the store so the backend
        // doesn't race the in-flight change_my_org POST when switching orgs.
        const orgId = this.$store && this.$store.state ? this.$store.state.activeOrganisationId : null
        if (orgId) {
          params.organisation_id = orgId
        }
      }
      if (tab && tab.careOfOnly) {
        params.care_of_only = 1
        if (this.careOfFilter.care_of_user && this.careOfFilter.care_of_user !== 'any') {
          params.care_of = this.careOfFilter.care_of_user
        }
        if (this.careOfFilter.start_date) {
          params.start_date = this.careOfFilter.start_date
        }
        if (this.careOfFilter.end_date) {
          params.end_date = this.careOfFilter.end_date
        }
      }
      if (tab && tab.trackingPending) {
        params.tracking_pending = 1
      }
      if (tab && tab.discountedOnly) {
        params.discounted_only = 1
      }
      return params
    },
    async applyCareOfFilter () {
      this.activePage = 1
      await this.fetchCurrentTabData()
    },
    resetCareOfFilter () {
      this.careOfFilter.care_of_user = 'any'
      this.careOfFilter.start_date = ''
      this.careOfFilter.end_date = ''
      this.activePage = 1
      this.fetchCurrentTabData()
    },
    async searchOrders () {
      // Filters (user, order type, bottles range, date range) are useful on
      // their own — don't force the user to also type a search value just to
      // run a filtered query. Backend skips the LIKE clause when value is empty.
      const hasAnyFilter = this.searchForm.search_value
        || (this.searchForm.organisation && this.searchForm.organisation !== 'all')
        || (this.searchForm.user && this.searchForm.user !== 'any')
        || (this.searchForm.care_of && this.searchForm.care_of !== 'any')
        || this.searchForm.product
        || (this.searchForm.order_type && this.searchForm.order_type !== 'any')
        || this.searchForm.payment_method
        || this.searchForm.order_status
        || this.searchForm.shipping_status
        || this.searchForm.payment_status
        || this.searchForm.start_date
        || this.searchForm.end_date

      if (!hasAnyFilter) {
        this.errorMessage = 'Enter a search value or pick a filter to continue.'
        this.orders = []
        this.maxPages = 1
        return
      }

      this.errorMessage = ''
      this.isLoading = true
      // Deliberately do NOT clear the selection here: searching again (e.g.
      // map-2500 then map-2600) should accumulate checked orders across
      // queries, not reset them. Selection is keyed by id in
      // `selectedOrdersById`, so it carries over independently of `orders`.
      this.resetSummary()

      const payload = {
        search_parameter: this.searchForm.search_parameter,
        search_value: this.searchForm.search_value,
        start_date: this.searchForm.start_date,
        end_date: this.searchForm.end_date,
        minBottles: this.searchForm.minBottles || 1,
        maxBottles: this.searchForm.maxBottles || 1000,
        organisation: this.searchForm.organisation,
        user: this.searchForm.user,
        care_of: this.searchForm.care_of,
        product: this.searchForm.product,
        order_type: this.searchForm.order_type,
        payment_method: this.searchForm.payment_method,
        order_status: this.searchForm.order_status,
        shipping_status: this.searchForm.shipping_status,
        payment_status: this.searchForm.payment_status,
        state_name: this.searchForm.state_name,
        district_name: this.searchForm.district_name,
        local_body_name: this.searchForm.local_body_name
      }

      try {
        const response = await axios.post(`${this.apiBase()}/api/orders/search_orders`, payload, {
          params: this.authParams()
        })
        this.consumeOrderResponse(response.data, { paginated: false })
      } catch (error) {
        this.orders = []
        this.maxPages = 1
        this.errorMessage = this.extractErrorMessage(error, 'Search failed. Please try again.')
      } finally {
        this.isLoading = false
      }
    },
    buildDateRangePayload (forDownload) {
      if (!this.dateFilter.start_date || !this.dateFilter.end_date) {
        throw new Error('Please select both start and end date.')
      }

      const payload = {
        start_date: this.dateFilter.start_date,
        end_date: this.dateFilter.end_date
      }

      if (['returned', 'cancelled', 'my_cancelled'].includes(this.activeTab)) {
        payload.type = forDownload ? 'download' : 'view'
      }

      return payload
    },
    consumeOrderResponse (payload, tabConfig) {
      const isPaginated = Boolean(tabConfig && tabConfig.paginated)

      if (this.activeTab === 'returned' && payload && typeof payload === 'object') {
        this.returnSummary = {
          flipkart_returned: Number(payload.flipkart_returned || 0),
          amazon_returned: Number(payload.amazon_returned || 0),
          ip_returned: Number(payload.ip_returned || 0),
          grand_total_amount: Number(payload.grand_total_amount || 0)
        }
      } else {
        this.resetSummary()
      }

      if (typeof payload === 'string') {
        this.orders = []
        this.maxPages = 1
        return
      }

      if (isPaginated && payload && Array.isArray(payload.data)) {
        this.orders = payload.data
        this.maxPages = payload.last_page || 1
        return
      }

      if (isPaginated && payload && payload.data && Array.isArray(payload.data.data)) {
        this.orders = payload.data.data
        this.maxPages = payload.data.last_page || 1
        return
      }

      if (Array.isArray(payload)) {
        this.orders = payload
        this.maxPages = 1
        return
      }

      if (payload && Array.isArray(payload.orders)) {
        this.orders = payload.orders
        this.maxPages = 1
        return
      }

      this.orders = []
      this.maxPages = 1
    },
    resetSummary () {
      this.returnSummary = {
        flipkart_returned: 0,
        amazon_returned: 0,
        ip_returned: 0,
        grand_total_amount: 0
      }
    },
    clearSelection () {
      this.selectedOrdersById = {}
    },
    deselectOrder (id) {
      const next = { ...this.selectedOrdersById }
      delete next[id]
      this.selectedOrdersById = next
    },
    toggleSelection (order, checked) {
      if (!this.canSelectRows) {
        return
      }
      // Reassign the map (rather than mutating keys) so Vue 2 picks up the
      // change reactively without needing $set/$delete.
      if (checked) {
        this.selectedOrdersById = { ...this.selectedOrdersById, [order.id]: order }
        return
      }
      const next = { ...this.selectedOrdersById }
      delete next[order.id]
      this.selectedOrdersById = next
    },
    toggleSelectAll (checked) {
      if (!this.canSelectRows) {
        return
      }
      if (checked) {
        // Merge the current page into the existing selection rather than
        // replacing it, so orders selected from a previous search stay checked.
        const next = { ...this.selectedOrdersById }
        this.orders.forEach(order => { next[order.id] = order })
        this.selectedOrdersById = next
        return
      }
      // Unchecking "select all" only clears the orders currently on screen,
      // leaving selections from other searches intact.
      const next = { ...this.selectedOrdersById }
      this.orders.forEach(order => { delete next[order.id] })
      this.selectedOrdersById = next
    },
    async downloadTabExport () {
      if (!this.canExportCurrentTab) {
        return
      }

      this.errorMessage = ''
      this.isLoading = true

      try {
        const response = await axios({
          url: `${this.apiBase()}${this.activeTabConfig.endpoint}`,
          method: 'POST',
          data: this.buildDateRangePayload(true),
          params: this.authParams(),
          responseType: 'blob'
        })

        const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = blobUrl
        link.setAttribute('download', `${this.activeTab}_orders.csv`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(blobUrl)
      } catch (error) {
        this.errorMessage = this.extractErrorMessage(error, 'Export failed. Please try again.')
      } finally {
        this.isLoading = false
      }
    },
    printInvoices () {
      if (!this.selectedOrderIds.length) {
        return
      }
      const url = `${this.apiBase()}/invoices?orders=${this.selectedOrderIds.join(',')}`
      window.open(url, '_blank')
    },
    printLabels () {
      if (!this.selectedOrderIds.length) {
        return
      }

      // Read from the persisted selection map, not `this.orders`, so orders
      // selected during an earlier search (no longer in the visible list)
      // are still included in the print job.
      const selectedOrders = Object.values(this.selectedOrdersById)
      const pickrrIds = selectedOrders
        .filter(order => order.shipping_method === 'pickrr' && order.aggregator_order_id_pk)
        .map(order => order.id)
      const otherIds = selectedOrders
        .filter(order => !(order.shipping_method === 'pickrr' && order.aggregator_order_id_pk))
        .map(order => order.id)

      if (otherIds.length) {
        this.openPrintWindow(`${this.apiBase()}/post-office-labels?orders=${otherIds.join(',')}`)
      }

      if (pickrrIds.length) {
        this.openPrintWindow(`${this.apiBase()}/pickrr-labels?orders=${pickrrIds.join(',')}`)
      }
    },
    openPrintWindow (url) {
      // The label routes live behind auth:sanctum (web.php), so a plain
      // `window.open(url)` browser navigation arrives without the Bearer
      // token and gets redirected to the POST-only /auth/login, surfacing
      // as MethodNotAllowed. Pre-opening a blank window, fetching the
      // rendered HTML through axios (which carries the token), and
      // writing the response into the window matches the working flow
      // used by PackingBatches.vue. /invoices stays on plain window.open
      // because it's a public route — no auth involved.
      const win = window.open('', '_blank')
      if (!win) {
        this.errorMessage = 'Pop-up blocked. Please allow pop-ups for this site and try again.'
        return
      }
      win.document.write('<!doctype html><title>Loading…</title><body style="font-family:system-ui;padding:2rem;color:#475569">Loading…</body>')
      axios.get(url, { params: this.authParams() })
        .then((response) => {
          win.document.open()
          win.document.write(response.data)
          win.document.close()
        })
        .catch((error) => {
          const message = this.extractErrorMessage(error, 'Failed to load print preview.')
          win.document.open()
          win.document.write(`<!doctype html><title>Error</title><body style="font-family:system-ui;padding:2rem;color:#991b1b">${message}</body>`)
          win.document.close()
        })
    },
    openViewModal (order) {
      this.selectedOrder = order
      this.resetProofAttachState()
      this.closeProofPreview()
      this.showDetailsModal = true
      // Load the org's discount tiers so Discount % can be expressed against
      // the discountable base (not the diluted discount / total). Cached, so
      // this is a no-op after the first order from the same org. The label
      // re-renders reactively once the cache populates.
      this.ensureDiscountPresets(order && order.organisation_id)
    },
    // History action: show every past order this customer placed. Reuses the
    // TeleSales "Contact History" fetch (contactController::searchCropsByPhone),
    // which returns the customer's full order list (order_items eager-loaded,
    // newest first) in one call. `cross_org: true` is sent intentionally so the
    // modal shows the customer's orders across ALL organisations, not just the
    // active one — a customer often buys from more than one org and the whole
    // relationship should be visible here. NOTE: the backend flag is ungated, so
    // any role that can open this modal sees cross-org data (accepted trade-off).
    // Spinner lives on the button (historyLoadingId) until the data arrives.
    async openHistoryModal (order) {
      if (this.historyLoadingId) return
      // Backend matches on the last 10 digits (substr($phone, -10)), so strip
      // any formatting first to guarantee a clean digit run.
      const phone = String(order.contact_phone || '').replace(/\D/g, '')
      if (!phone) {
        this.errorMessage = 'This order has no phone number to look up history.'
        return
      }
      this.historyLoadingId = order.id
      try {
        const response = await axios.get(
          `${this.apiBase()}/api/contacts/phone/${encodeURIComponent(phone)}`,
          // include_trashed: surface cancelled (soft-deleted) orders too — and
          // the current order, even when it was cancelled.
          { params: this.authParams({ cross_org: true, include_trashed: 1 }) }
        )
        const data = response.data || {}
        // The customer's full order + call history (across all orgs). The summary
        // cards read totals over the whole list, so keep every order here —
        // including the one on screen, which is a real order of theirs.
        this.historyOrders = Array.isArray(data.orders) ? data.orders : []
        this.historyCalls = Array.isArray(data.calls) ? data.calls : []
        this.historyCustomer = {
          name: order.contact_name || '—',
          phone: order.contact_phone || ''
        }
        this.showHistoryModal = true
      } catch (error) {
        this.errorMessage = this.extractErrorMessage(error, 'Could not load order history.')
      } finally {
        this.historyLoadingId = null
      }
    },
    // Hand off from a history row to the full order-detail modal. Close History
    // first so the two lg modals never stack.
    openHistoryOrderDetail (order) {
      this.showHistoryModal = false
      this.openViewModal(order)
    },
    // Normalise a stored phone for display: contact numbers land as "09876…",
    // "+919876…", "919876…" etc. Collapse to the last 10 digits and group as
    // "98765 43210" (same convention as TheHeaderCallQuickView.formatPhone) so
    // the modal header reads consistently regardless of how it was saved.
    formatPhone (raw) {
      const digits = String(raw || '').replace(/\D/g, '').slice(-10)
      if (digits.length !== 10) return String(raw || '')
      return `${digits.slice(0, 5)} ${digits.slice(5)}`
    },
    // Refund badge for a history row, reflecting the order's refund records
    // (refunds.status ∈ approval_pending | completed | rejected). Precedence:
    // a completed refund wins ("Refunded"), else a pending one ("Refund
    // Pending"); a lone rejected request (or none) shows nothing — the order
    // was not refunded. Returns null when there's no badge to show.
    historyRefundBadge (order) {
      const refunds = (order && order.refunds) || []
      if (!refunds.length) return null
      if (refunds.some(r => r && r.status === 'completed')) {
        return { label: 'Refunded', cls: 'badge--green' }
      }
      if (refunds.some(r => r && r.status === 'approval_pending')) {
        return { label: 'Refund Pending', cls: 'badge--yellow' }
      }
      return null
    },
    // Compact one-line product summary for a history row (e.g. "Product A ×2,
    // Product B ×1"). Truncated in the cell via title tooltip for the rest.
    historyItemsSummary (order) {
      const items = (order && order.order_items) || []
      if (!items.length) return '—'
      return items.map(item => `${this.itemLabel(item)} ×${item.quantity || 0}`).join(', ')
    },
    resetProofAttachState () {
      this.proofNewScreenshots = []
      this.proofUploading = 0
      this.proofSaving = false
      this.proofError = ''
      this.proofDelete = { visible: false, shot: null, message: '' }
      this.proofDeleting = false
    },
    askDeleteSavedProof (shot) {
      if (!shot || this.proofDeleting) return
      this.proofDelete = {
        visible: true,
        shot,
        message: `Remove ${shot.name ? `“${shot.name}”` : 'this screenshot'} from the order? The file is also deleted from storage — this can’t be undone.`
      }
    },
    async confirmDeleteSavedProof () {
      const order = this.selectedOrder
      const shot = this.proofDelete.shot
      if (!order || !order.id || !shot) {
        this.proofDelete = { visible: false, shot: null, message: '' }
        return
      }

      this.proofDeleting = true
      this.proofError = ''
      try {
        const response = await axios.delete(
          `${this.apiBase()}/api/orders/${order.id}/payment_proof`,
          { data: { key: shot.key || null, url: shot.url || null }, params: this.authParams() }
        )
        const updated = response.data || {}
        // Commit the trimmed array onto the open order and the listing row,
        // same as saveProofScreenshots does after an append.
        this.$set(this.selectedOrder, 'payment_screenshots', updated.payment_screenshots || [])
        const idx = this.orders.findIndex(o => o.id === order.id)
        if (idx !== -1) {
          this.$set(this.orders, idx, { ...this.orders[idx], payment_screenshots: updated.payment_screenshots || [] })
        }
        // Keep the lightbox coherent if it was open on a now-removed slide.
        if (this.showProofPreview) {
          const len = this.selectedOrderScreenshots.length
          if (!len) this.closeProofPreview()
          else if (this.proofPreviewIndex >= len) this.proofPreviewIndex = len - 1
        }
        this.proofDelete = { visible: false, shot: null, message: '' }
      } catch (error) {
        this.proofError = this.extractErrorMessage(error, 'Failed to delete the screenshot.')
        this.proofDelete = { visible: false, shot: null, message: '' }
      } finally {
        this.proofDeleting = false
      }
    },
    // Payment-proof lightbox — mirrors the gallery preview used in TeleSales.
    isPdfAttachment (file) {
      if (!file) return false
      if (file.mime === 'application/pdf') return true
      return /\.pdf($|\?)/i.test(String(file.url || file.name || ''))
    },
    triggerProofUpload () {
      const input = this.$refs.proofFileInput
      if (input) input.click()
    },
    async onProofFilesSelected (event) {
      const input = event.target
      const files = Array.from(input.files || [])
      input.value = ''
      if (!files.length) return

      this.proofError = ''
      for (const file of files) {
        await this.uploadProofScreenshot(file)
      }
    },
    async uploadProofScreenshot (file) {
      // Mirror the server validation (PaymentScreenshotController): images + PDF
      // only, max 10MB. Fail fast client-side so the user gets a clear message.
      const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf']
      if (!allowed.includes(file.type)) {
        this.proofError = `${file.name}: unsupported type. Upload an image or PDF.`
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        this.proofError = `${file.name}: exceeds the 10MB limit.`
        return
      }

      this.proofUploading += 1
      try {
        const fd = new FormData()
        fd.append('file', file)
        const { data } = await axios.post(
          `${this.apiBase()}/api/payment-screenshots/upload`,
          fd,
          { params: this.authParams(), headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }
        )
        this.proofNewScreenshots.push({
          url: data.url,
          key: data.key,
          name: data.name || file.name,
          mime: data.mime || file.type,
          size: data.size || file.size
        })
      } catch (error) {
        this.proofError = this.extractErrorMessage(error, `${file.name}: upload failed. Please try again.`)
      } finally {
        this.proofUploading -= 1
      }
    },
    removeNewProofScreenshot (newIndex) {
      this.proofNewScreenshots.splice(newIndex, 1)
      // Keep the lightbox coherent if it's open on a slide that just shifted.
      if (this.showProofPreview) {
        if (!this.selectedOrderScreenshots.length) {
          this.closeProofPreview()
        } else if (this.proofPreviewIndex >= this.selectedOrderScreenshots.length) {
          this.proofPreviewIndex = this.selectedOrderScreenshots.length - 1
        }
      }
    },
    async saveProofScreenshots () {
      if (!this.selectedOrder || this.proofSaving || !this.proofNewScreenshots.length) return

      this.proofSaving = true
      this.proofError = ''
      try {
        const payload = {
          payment_screenshots: this.proofNewScreenshots.map(f => ({
            url: f.url, key: f.key, name: f.name, mime: f.mime, size: f.size
          }))
        }
        const response = await axios.post(
          `${this.apiBase()}/api/orders/${this.selectedOrder.id}/payment_proof`,
          payload,
          { params: this.authParams() }
        )
        const updated = response.data || {}
        // Commit the merged array onto the open order and the listing row so
        // the saved gallery reflects the new attachments without a tab refresh.
        this.$set(this.selectedOrder, 'payment_screenshots', updated.payment_screenshots || [])
        const idx = this.orders.findIndex(o => o.id === this.selectedOrder.id)
        if (idx !== -1) {
          this.$set(this.orders, idx, { ...this.orders[idx], payment_screenshots: updated.payment_screenshots || [] })
        }
        this.proofNewScreenshots = []
      } catch (error) {
        this.proofError = this.extractErrorMessage(error, 'Failed to save screenshots.')
      } finally {
        this.proofSaving = false
      }
    },
    openProofPreview (index) {
      if (!this.selectedOrderScreenshots.length) return
      this.proofPreviewIndex = Math.min(Math.max(index, 0), this.selectedOrderScreenshots.length - 1)
      this.showProofPreview = true
    },
    closeProofPreview () {
      this.showProofPreview = false
      this.proofPreviewIndex = 0
    },
    stepProofPreview (delta) {
      const len = this.selectedOrderScreenshots.length
      if (!len) return
      this.proofPreviewIndex = (this.proofPreviewIndex + delta + len) % len
    },
    handleProofPreviewKeydown (event) {
      if (!this.showProofPreview) return
      if (event.key === 'Escape') this.closeProofPreview()
      else if (event.key === 'ArrowLeft') this.stepProofPreview(-1)
      else if (event.key === 'ArrowRight') this.stepProofPreview(1)
    },
    canShareTracking (order) {
      // Only show the WhatsApp affordance when there's actually something
      // to send AND someone to send it to. Saves telesales from clicking
      // through to a half-empty message.
      if (!order) return false
      const hasTracking = Boolean(order.tracking_id && String(order.tracking_id).trim())
      const hasPhone = Boolean(order.contact_phone && String(order.contact_phone).replace(/\D/g, '').length >= 10)
      return hasTracking && hasPhone
    },
    courierTrackingUrl (order) {
      if (!order) return ''
      const id = encodeURIComponent(order.tracking_id || '')
      switch (order.shipping_method) {
        case 'ip_speed':
        case 'ip_registered_parcel':
          return `https://myspeedpost.com/?n=${id}`
        case 'dtdc':
          return `https://www.dtdc.in/tracking/tracking_results.asp?strCnno=${id}`
        default:
          return ''
      }
    },
    sendTrackingViaWhatsapp (order) {
      if (!this.canShareTracking(order)) return

      // Strip non-digits and prefix India country code if a 10-digit local
      // number was stored. wa.me requires the number in international form
      // without the leading + sign.
      const phoneDigits = String(order.contact_phone).replace(/\D/g, '')
      const e164 = phoneDigits.length === 10 ? `91${phoneDigits}` : phoneDigits

      const firstName = order.contact_name ? String(order.contact_name).split(/\s+/)[0] : 'Customer'
      const orderNumber = order.order_number || `#${order.id}`
      const courier = this.shippingMethodLabel(order.shipping_method)
      const trackingUrl = this.courierTrackingUrl(order)

      const lines = [
        `Hi ${firstName},`,
        '',
        `Your order ${orderNumber} has been shipped via ${courier}.`,
        `Tracking ID: ${order.tracking_id}`
      ]
      if (trackingUrl) {
        lines.push(`Track here: ${trackingUrl}`)
      }
      lines.push('', 'Thank you for shopping with us!')

      const message = encodeURIComponent(lines.join('\n'))
      window.open(`https://wa.me/${e164}?text=${message}`, '_blank', 'noopener')

      // Optimistically record the send so the order leaves the Pending
      // Tracking queue and the counters tick. Best we can do without
      // WhatsApp Business API — wa.me has no callback for "actually sent".
      this.markTrackingNotified(order, true)
    },
    async markTrackingNotified (order, notified) {
      if (!order || !order.id) return

      // Optimistic update FIRST so the button flips to "Sent" immediately.
      // Use this.$set on the property (rather than swapping the whole
      // object) — when an existing row was loaded WITHOUT a
      // `tracking_notified_at` key (older API responses pre-migration),
      // that property is non-reactive on the existing object reference,
      // and a plain assignment won't trigger a re-render.
      const optimisticTimestamp = notified ? new Date().toISOString() : null
      const idx = this.orders.findIndex(o => Number(o.id) === Number(order.id))
      const previousValue = idx !== -1 ? this.orders[idx].tracking_notified_at : null

      if (idx !== -1) {
        if (this.activeTab === 'tracking_pending' && notified) {
          // The row no longer matches the Pending Tracking filter; drop
          // it from the local list so the queue shrinks visibly.
          this.orders.splice(idx, 1)
        } else {
          this.$set(this.orders[idx], 'tracking_notified_at', optimisticTimestamp)
        }
      }
      if (this.selectedOrder && Number(this.selectedOrder.id) === Number(order.id)) {
        this.$set(this.selectedOrder, 'tracking_notified_at', optimisticTimestamp)
      }

      try {
        const response = await axios.post(
          `${this.apiBase()}/api/orders/${order.id}/mark_tracking_notified`,
          { notified: notified ? 1 : 0 },
          { params: this.authParams() }
        )
        const updated = response.data || {}

        // Reconcile with the server's authoritative timestamp.
        const reconcileIdx = this.orders.findIndex(o => Number(o.id) === Number(order.id))
        if (reconcileIdx !== -1 && updated.id) {
          Object.keys(updated).forEach(key => {
            this.$set(this.orders[reconcileIdx], key, updated[key])
          })
        }
        if (this.selectedOrder && Number(this.selectedOrder.id) === Number(order.id) && updated.id) {
          Object.keys(updated).forEach(key => {
            this.$set(this.selectedOrder, key, updated[key])
          })
        }
        if (this.activeTab === 'tracking_pending') {
          this.fetchTrackingStats()
        }
      } catch (error) {
        console.error('mark_tracking_notified failed:', error?.response?.status, error?.response?.data || error?.message)
        const rollbackIdx = this.orders.findIndex(o => Number(o.id) === Number(order.id))
        if (rollbackIdx !== -1) {
          this.$set(this.orders[rollbackIdx], 'tracking_notified_at', previousValue)
        }
        if (this.selectedOrder && Number(this.selectedOrder.id) === Number(order.id)) {
          this.$set(this.selectedOrder, 'tracking_notified_at', previousValue)
        }
      }
    },
    async downloadCustomerInvoice (order) {
      if (!order || !order.id) return
      // Pull the server-rendered PDF as a blob and save it directly, so the
      // staff gets an actual .pdf download (not a browser print dialog). The
      // request carries the Bearer token via the global axios interceptor.
      if (this.downloadingInvoiceId === order.id) return
      this.downloadingInvoiceId = order.id
      try {
        const response = await axios.get(
          `${this.apiBase()}/api/orders/${order.id}/invoice_pdf`,
          { responseType: 'blob', params: this.authParams() }
        )
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const blobUrl = window.URL.createObjectURL(blob)
        const label = order.invoice_number || order.order_number || `Order-${order.id}`
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = `Invoice-${String(label).replace(/[^A-Za-z0-9_-]/g, '')}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)
      } catch (error) {
        console.error('invoice_pdf download failed:', error?.response?.status, error?.message)
        this.errorMessage = 'Could not download the invoice PDF. Please try again.'
      } finally {
        this.downloadingInvoiceId = null
      }
    },
    canSendInvoice (order) {
      // Need a customer phone to send to. Mirrors canShareTracking.
      if (!order) return false
      return Boolean(order.contact_phone && String(order.contact_phone).replace(/\D/g, '').length >= 10)
    },
    buildCustomerInvoiceMessage (order) {
      // Customer-facing invoice message — same shape as the TeleSales
      // convert-to-sale "Send to WhatsApp" text (buildSaleWhatsappMessage):
      // greeting, line items, cost breakdown, total, payment, invoice link.
      const customer = order.contact_name ? String(order.contact_name) : 'Customer'
      const lines = [`Hello ${customer},`, '', 'Thank you for your order. Here are the details:']
      const items = Array.isArray(order.order_items) ? order.order_items : []
      items.forEach((item) => {
        const qty = Number(item.quantity || 0)
        lines.push(`${qty} × ${this.itemLabel(item)} — ${this.currency(item.total)}`)
      })
      const discount = Number(order.discount || 0)
      // Customer-facing breakdown: show the delivery cost (base shipping plus
      // any COD handling) as a single "Shipping charge" line, and omit GST —
      // prices are GST-inclusive, so a separate GST line only confuses the
      // customer and the Total already accounts for it.
      const shipping = Number(order.shipping_charge || 0) + Number(order.cod_charge || 0)
      if (discount > 0) lines.push(`Discount: −${this.currency(discount)}`)
      if (shipping > 0) lines.push(`Shipping charge: ${this.currency(shipping)}`)
      lines.push(`*Total: ${this.currency(order.grand_total)}*`)
      if (order.payment_method) lines.push(`Payment: ${this.paymentMethodLabel(order.payment_method)}`)
      // No invoice link: staff download the PDF (Invoice button) and attach it
      // to the WhatsApp chat themselves, so a public /invoices URL in the
      // message body is redundant.
      return lines.join('\n')
    },
    sendInvoiceToCustomer (order) {
      if (!this.canSendInvoice(order)) return

      const phoneDigits = String(order.contact_phone).replace(/\D/g, '')
      const e164 = phoneDigits.length === 10 ? `91${phoneDigits}` : phoneDigits
      const message = encodeURIComponent(this.buildCustomerInvoiceMessage(order))
      window.open(`https://wa.me/${e164}?text=${message}`, '_blank', 'noopener')

      // Optimistically record the send so the order leaves the Pending
      // Invoices queue. wa.me gives no delivery callback, same as tracking.
      this.markInvoiceSent(order, true)
    },
    async markInvoiceSent (order, sent) {
      if (!order || !order.id) return

      const optimisticTimestamp = sent ? new Date().toISOString() : null
      const idx = this.orders.findIndex(o => Number(o.id) === Number(order.id))
      const previousValue = idx !== -1 ? this.orders[idx].invoice_sent_at : null

      // Keep the row in place and flip it to the "Sent" state rather than
      // removing it — the staff needs to SEE which invoices they've already
      // sent. The backend filters invoice_sent_at IS NULL, so sent rows fall
      // off the queue on the next refresh/reload, not mid-task.
      if (idx !== -1) {
        this.$set(this.orders[idx], 'invoice_sent_at', optimisticTimestamp)
      }
      if (this.selectedOrder && Number(this.selectedOrder.id) === Number(order.id)) {
        this.$set(this.selectedOrder, 'invoice_sent_at', optimisticTimestamp)
      }

      try {
        const response = await axios.post(
          `${this.apiBase()}/api/orders/${order.id}/mark_invoice_sent`,
          { sent: sent ? 1 : 0 },
          { params: this.authParams() }
        )
        const updated = response.data || {}
        const reconcileIdx = this.orders.findIndex(o => Number(o.id) === Number(order.id))
        if (reconcileIdx !== -1 && updated.id) {
          Object.keys(updated).forEach(key => {
            this.$set(this.orders[reconcileIdx], key, updated[key])
          })
        }
        if (this.selectedOrder && Number(this.selectedOrder.id) === Number(order.id) && updated.id) {
          Object.keys(updated).forEach(key => {
            this.$set(this.selectedOrder, key, updated[key])
          })
        }
      } catch (error) {
        console.error('mark_invoice_sent failed:', error?.response?.status, error?.response?.data || error?.message)
        // Roll back the optimistic "Sent" flag so the row reverts to its
        // unsent state and the staff can retry.
        const rollbackIdx = this.orders.findIndex(o => Number(o.id) === Number(order.id))
        if (rollbackIdx !== -1) {
          this.$set(this.orders[rollbackIdx], 'invoice_sent_at', previousValue)
        }
        if (this.selectedOrder && Number(this.selectedOrder.id) === Number(order.id)) {
          this.$set(this.selectedOrder, 'invoice_sent_at', previousValue)
        }
        this.errorMessage = 'Could not mark the invoice as sent. Please try again.'
      }
    },
    dropdownAsOptions (group, placeholder) {
      const options = (Array.isArray(group) ? group : []).map(item => ({
        value: String(item.slug || item.value || item.name || ''),
        label: item.name || item.label || item.slug || ''
      })).filter(opt => opt.value)
      return [
        { value: '', label: placeholder || 'Select…' },
        ...options
      ]
    },
    isoDateInput (value) {
      // Order timestamps come back in many shapes (ISO string, "YYYY-MM-DD",
      // unix seconds). The <input type="date"> needs strict YYYY-MM-DD, so
      // normalise via the same parser the table uses.
      if (!value) return ''
      const parsed = this.parseDate(value)
      if (!parsed || Number.isNaN(parsed.getTime())) return ''
      return this.toDateInput(parsed)
    },
    openEditModal (order) {
      if (!order) return
      this.editOrder = order
      this.editForm = {
        order_status: order.order_status || '',
        payment_status: order.payment_status || '',
        payment_method: order.payment_method || '',
        payment_date: this.isoDateInput(order.payment_date),
        shipping_status: order.shipping_status || '',
        shipping_method: order.shipping_method || '',
        shipping_date: this.isoDateInput(order.shipping_date),
        tracking_id: order.tracking_id || '',
        care_of: order.care_of ? String(order.care_of) : 'none',
        notes: order.notes || ''
      }
      this.editError = ''
      this.showEditModal = true
      // Closing the View modal keeps the modal stack clean if Edit was
      // opened from it; opening from a row is a no-op.
      this.showDetailsModal = false
    },
    closeEditModal () {
      this.showEditModal = false
      this.editOrder = null
      this.editForm = blankEditForm()
      this.editError = ''
    },
    goToFullEdit () {
      // Escalation path: when the user needs to change line items, address,
      // GST or any of the deeper fields the Quick Edit modal intentionally
      // doesn't expose, jump to OrderForm in edit mode.
      if (!this.editOrder) return
      const order = this.editOrder
      this.closeEditModal()
      this.$router.push({
        path: '/orderform',
        query: {
          order_id: order.id,
          order_number: order.order_number || ''
        }
      })
    },
    async submitEdit () {
      if (!this.editOrder || this.isSavingEdit) return

      this.isSavingEdit = true
      this.editError = ''

      const payload = {}
      QUICK_EDIT_FIELDS.forEach(field => {
        const value = this.editForm[field]
        if (field === 'care_of') {
          payload.care_of = value === 'none' || value === '' ? null : Number(value)
          return
        }
        payload[field] = value === '' ? null : value
      })

      try {
        const response = await axios.patch(
          `${this.apiBase()}/api/orders/${this.editOrder.id}/quick_update`,
          payload,
          { params: this.authParams() }
        )
        const updated = response.data || {}
        // Replace the row in place so the user sees the change without a
        // full tab refresh (which would also reset their pagination/filters).
        const idx = this.orders.findIndex(o => o.id === this.editOrder.id)
        if (idx !== -1) {
          this.$set(this.orders, idx, { ...this.orders[idx], ...updated })
        }
        this.closeEditModal()
      } catch (error) {
        this.editError = this.extractErrorMessage(error, 'Failed to save changes.')
      } finally {
        this.isSavingEdit = false
      }
    },
    isOrderCancelled (order) {
      if (!order) return false
      if (order.deleted_at) return true
      return String(order.order_status || '').toLowerCase() === 'cancelled'
    },
    isOrderReturned (order) {
      if (!order || this.isOrderCancelled(order)) return false
      const s = String(order.order_status || '').toLowerCase()
      return s === 'returned' || s === 'partially_returned'
    },
    isAmazonOrder (order) {
      return Boolean(order) && String(order.order_type || '').toLowerCase() === 'amazon'
    },
    displayOrderStatus (order) {
      if (this.isOrderCancelled(order)) return 'Cancelled'
      if (this.isOrderReturned(order)) {
        return String(order.order_status || '').toLowerCase() === 'partially_returned' ? 'Partially Returned' : 'Returned'
      }
      // Backend statuses are snake_case (e.g. pending_approval, confirmed) —
      // humanise the underscore so the pill reads "Pending approval", and the
      // pill's text-transform handles capitalise (table) / uppercase (modal).
      return order && order.order_status ? String(order.order_status).replace(/_/g, ' ') : '—'
    },
    displayOrderStatusClass (order) {
      if (this.isOrderCancelled(order)) return 'badge--red'
      if (this.isOrderReturned(order)) return 'badge--yellow'
      return this.orderStatusClass(order && order.order_status)
    },
    openCancelConfirm (order) {
      if (!order) return
      this.orderToCancel = order
      this.errorMessage = ''
      this.showCancelConfirm = true
    },
    closeCancelConfirm () {
      if (this.isCancelling) return
      this.showCancelConfirm = false
      this.orderToCancel = null
    },
    async confirmCancelOrder () {
      const order = this.orderToCancel
      if (!order || this.isCancelling) return

      this.isCancelling = true
      this.errorMessage = ''

      try {
        await axios.delete(`${this.apiBase()}/api/orders/${order.id}`, {
          params: this.authParams()
        })

        // Reflect the new state locally — flip the badge to cancelled and
        // hide the row's Cancel/Refund affordances on next render.
        const idx = this.orders.findIndex(o => o.id === order.id)
        if (idx !== -1) {
          this.$set(this.orders[idx], 'order_status', 'cancelled')
        }
        if (this.selectedOrder && this.selectedOrder.id === order.id) {
          this.$set(this.selectedOrder, 'order_status', 'cancelled')
        }

        this.showCancelConfirm = false
        this.orderToCancel = null
      } catch (error) {
        this.errorMessage = this.extractErrorMessage(error, 'Failed to cancel the order.')
      } finally {
        this.isCancelling = false
      }
    },
    openRefundModal (order) {
      if (!order) return
      this.refundOrder = order
      this.refundForm = {
        ...blankRefundForm(),
        name: order.contact_name || '',
        amount: Number(order.grand_total || 0)
      }
      this.refundError = ''
      this.showRefundModal = true
    },
    closeRefundModal () {
      // No isSavingRefund guard needed — the Cancel button is disabled and
      // the backdrop click is suppressed while saving, so the only callers
      // that can fire mid-save are programmatic (the post-submit success
      // path), and those must close the modal.
      this.showRefundModal = false
      this.refundOrder = null
      this.refundForm = blankRefundForm()
      this.refundError = ''
      if (this.$refs.refundProofInput) {
        this.$refs.refundProofInput.value = ''
      }
    },
    onRefundProofSelected (event) {
      const file = event.target.files && event.target.files[0]
      this.acceptRefundProof(file)
      event.target.value = ''
    },
    openRefundProofPicker () {
      if (this.isSavingRefund) return
      if (this.$refs.refundProofInput) this.$refs.refundProofInput.click()
    },
    onRefundProofDragOver () {
      if (this.isSavingRefund) return
      this.isRefundDragging = true
    },
    onRefundProofDragLeave () {
      this.isRefundDragging = false
    },
    onRefundProofDrop (event) {
      this.isRefundDragging = false
      if (this.isSavingRefund) return
      const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]
      this.acceptRefundProof(file)
    },
    acceptRefundProof (file) {
      if (!file) {
        this.refundForm.proofFile = null
        return
      }
      // Mirror the server validation: image/* OR application/pdf, max 5 MB.
      // Surfacing a clear inline error is friendlier than letting the multipart
      // POST fail server-side after the user clicks Submit.
      const isImage = file.type && file.type.startsWith('image/')
      const isPdf = file.type === 'application/pdf'
      if (!isImage && !isPdf) {
        this.refundError = 'Proof must be an image or a PDF.'
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        this.refundError = 'Proof must be 5 MB or smaller.'
        return
      }
      this.refundError = ''
      this.refundForm.proofFile = file
    },
    clearRefundProof () {
      if (this.isSavingRefund) return
      this.refundForm.proofFile = null
      if (this.$refs.refundProofInput) this.$refs.refundProofInput.value = ''
    },
    async submitRefund () {
      if (!this.refundOrder || this.isSavingRefund || !this.isRefundFormValid) return

      this.isSavingRefund = true
      this.refundError = ''

      // RefundController.store accepts multipart/form-data because of the
      // optional proofFile upload. FormData is the simplest way to do that.
      const fd = new FormData()
      // Always send order_id — it's the primary key, always present and
      // unambiguous. order_number is included as a fallback display key but
      // the server prefers order_id when both are sent.
      fd.append('order_id', String(this.refundOrder.id))
      fd.append('order_number', this.refundOrder.order_number || '')
      fd.append('name', this.refundForm.name.trim())
      fd.append('amount', String(this.refundForm.amount))
      fd.append('bank_account_no', this.refundForm.bank_account_no.trim())
      fd.append('IFSC', this.refundForm.IFSC.trim().toUpperCase())
      fd.append('reason', this.refundForm.reason.trim())
      if (this.refundForm.proofFile) {
        fd.append('proofFile', this.refundForm.proofFile)
      }

      try {
        const response = await axios.post(`${this.apiBase()}/api/refund`, fd, {
          params: this.authParams(),
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        // RefundController returns a plain {message} body on the 'order does
        // not exist' branch with a 200 status — guard for that.
        const body = response.data || {}
        if (!body.refund && body.message && /does not exist/i.test(body.message)) {
          this.refundError = body.message
          return
        }
        this.closeRefundModal()
      } catch (error) {
        this.refundError = this.extractErrorMessage(error, 'Failed to submit refund request.')
      } finally {
        this.isSavingRefund = false
      }
    },
    orderStatusClass (status) {
      const s = String(status || '').toLowerCase()
      if (s === 'confirmed') return 'badge--green'
      if (s === 'cancelled') return 'badge--red'
      if (s === 'on_hold' || s === 'pending') return 'badge--yellow'
      return 'badge--gray'
    },
    paymentBadgeClass (status) {
      return status === 'payment_received' ? 'badge--green' : 'badge--yellow'
    },
    soldByLabel (order) {
      return order.user && order.user.name ? order.user.name : '-'
    },
    careOfLabel (order) {
      // `care_of` is a users.id pointing to the telesales staff whose call
      // brought this customer in (see POS billing flow). 0 / null = none.
      const id = order && order.care_of
      if (!id) return ''
      if (order.care_of_user && order.care_of_user.name) return order.care_of_user.name
      if (order.care_of_name) return order.care_of_name
      const found = this.users.find(user => Number(user.id) === Number(id))
      return found ? found.name : `#${id}`
    },
    // Builds the same WhatsApp/accounts-team message TeleSales produces
    // (buildWhatsappShareMessage), but sourced from a saved order record
    // instead of the live form. Keep this aligned with TeleSales.
    buildAccountsMessage (order) {
      if (!order) return ''
      // Order date as dd/mm/yyyy — prefer the canonical order_date, fall back
      // to created_at (a Unix-seconds timestamp on the Order model).
      const parsed = this.parseDate(order.order_date || order.created_at)
      let date = ''
      if (parsed && !Number.isNaN(parsed.getTime())) {
        const dd = String(parsed.getDate()).padStart(2, '0')
        const mm = String(parsed.getMonth() + 1).padStart(2, '0')
        date = `${dd}/${mm}/${parsed.getFullYear()}`
      }

      // Tele caller — the telesales credit (care_of) when present, else the
      // staff who placed the order.
      const sold = this.soldByLabel(order)
      const caller = String(this.careOfLabel(order) || (sold !== '-' ? sold : '')).trim().toUpperCase()

      const customerName = String(order.contact_name || '').trim().toUpperCase()
      const phone = order.contact_phone ? String(order.contact_phone).trim() : ''
      const altPhone = order.contact_phone_2 ? String(order.contact_phone_2).trim() : ''

      const addressParts = [
        order.contact_address,
        order.contact_postofficename,
        order.contact_district,
        order.contact_state
      ].map((part) => String(part || '').trim()).filter(Boolean)
      const pincode = order.contact_pincode ? String(order.contact_pincode).trim() : ''
      const address = (pincode ? [...addressParts, pincode] : addressParts).join(', ').toUpperCase()

      const items = Array.isArray(order.order_items) ? order.order_items : []
      const totalQty = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0)

      // Tag each line with the discount it earned so a mixed-discount order
      // (e.g. one product at 35%, another at 10%) reads clearly instead of
      // collapsing to a single blended %. Same reconciled replay the Items
      // column and the detail table use — null when it can't be trusted.
      const discountAmt = Number(order.discount || 0)
      const breakdown = this.resolveLineDiscounts(order)
      const taggedPercents = breakdown
        ? breakdown.filter((b) => Number(b.percent) > 0).map((b) => b.percent)
        : []
      const hasMixedDiscount = new Set(taggedPercents).size > 1

      const productNames = items
        .map((item, idx) => {
          const name = String(this.itemLabel(item) || '').trim()
          if (!name) return ''
          const qty = Number(item.quantity || 0)
          const disc = breakdown ? breakdown[idx] : null
          const tag = disc && Number(disc.percent) > 0 ? ` — ${disc.percent}% off` : ''
          // Line MRP (qty × rate), so the per-line figures sum to the MRP
          // total in the summary block below and accounts can reconcile.
          // itemRowTotal falls back to price × qty and can return '—'.
          const lineMrp = Number(this.itemRowTotal(item))
          const mrpTag = Number.isFinite(lineMrp) && lineMrp > 0 ? ` — MRP ${Math.round(lineMrp)}` : ''
          return `${name} x ${qty}${tag}${mrpTag}`
        })
        .filter(Boolean)
        .join('\n')

      const paymentMethod = order.payment_method ? this.paymentMethodLabel(order.payment_method).toUpperCase() : ''
      const shippingMethod = order.shipping_method ? this.shippingMethodLabel(order.shipping_method).toUpperCase() : ''

      const lines = []
      if (date) lines.push(`Date : ${date}`)
      lines.push(`Tele caller : ${caller}`)
      if (order.order_number) lines.push(`Order number : ${order.order_number}`)
      lines.push(
        '',
        '',
        'Customer Name:',
        customerName,
        'Customer Phone:',
        phone,
        ...(altPhone ? ['Alternate Phone:', altPhone] : []),
        'Customer Address:',
        address,
        `Product quantity ${totalQty}`,
        '',
        'Product name :',
        productNames,
        ''
      )

      // Money block for the accounts team. Two totals, deliberately:
      //   Amount (Excl Shipping) = MRP − Discount  — the products figure
      //   Total Amount           = grand_total     — what the customer pays
      // `order.total` is the products subtotal and already excludes
      // shipping/COD; those live in grand_total. Splitting the two lets
      // accounts reconcile the product maths and still see the collectible
      // amount. When the order carries mixed per-product discounts (tagged
      // inline on each line above), the % is flagged as blended so nobody
      // reads it as one flat rate applied to everything.
      const subtotal = Number(order.total || 0)
      if (subtotal > 0) {
        const discountPercent = discountAmt > 0
          ? Number(((discountAmt / subtotal) * 100).toFixed(2))
          : 0
        const netAmount = subtotal - discountAmt
        const grandTotal = Number(order.grand_total || 0) || netAmount
        // Derived from the two totals rather than read from shipping_charge
        // directly, so the block always closes — this figure is whatever
        // grand_total actually adds on top (shipping + any COD charge).
        const shippingCharge = grandTotal - netAmount
        lines.push(
          `MRP: ${Math.round(subtotal)}`,
          `Discount Amount: ${discountAmt > 0 ? '-' : ''}${Math.round(discountAmt)}`,
          `Discount %: ${discountPercent}${hasMixedDiscount ? ' (mixed)' : ''}`,
          `Amount (Excl Shipping): ${Math.round(netAmount)}`,
          `Shipping Charge: ${Math.round(shippingCharge)}`,
          `Total Amount: ${Math.round(grandTotal)}`,
          ''
        )
      }

      lines.push(
        `Payment method. ${paymentMethod}`,
        `Shipping method. ${shippingMethod}`
      )

      return lines.join('\n')
    },
    async ensureDiscountPresets (organisationId) {
      const orgId = Number(organisationId) || null
      if (!orgId) return []
      if (this.discountPresetsByOrg[orgId]) return this.discountPresetsByOrg[orgId]
      try {
        const response = await axios.get(`${this.apiBase()}/api/discount-presets/by-org/${orgId}`, {
          params: this.authParams(),
          timeout: 10000
        })
        const tiers = Array.isArray(response.data) ? response.data : []
        // Cache reactively so a re-copy of any order in the same org is instant.
        this.$set(this.discountPresetsByOrg, orgId, tiers)
        return tiers
      } catch (e) {
        // Presets unavailable — fall back to a message without the per-line
        // split (buildAccountsMessage reconciles against this and tags nothing).
        return []
      }
    },
    async copyOrderForAccounts (order) {
      // Warm the cache first — buildAccountsMessage reads the tiers back out
      // of discountPresetsByOrg via resolveLineDiscounts.
      await this.ensureDiscountPresets(order && order.organisation_id)
      const message = this.buildAccountsMessage(order)
      if (!message) return
      this.errorMessage = ''

      let copied = false
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(message)
          copied = true
        }
      } catch (e) {
        copied = false
      }
      // Fallback for non-secure contexts / older browsers where the async
      // Clipboard API is unavailable.
      if (!copied) {
        try {
          const ta = document.createElement('textarea')
          ta.value = message
          ta.setAttribute('readonly', '')
          ta.style.position = 'absolute'
          ta.style.left = '-9999px'
          document.body.appendChild(ta)
          ta.select()
          copied = document.execCommand('copy')
          document.body.removeChild(ta)
        } catch (e) {
          copied = false
        }
      }

      if (copied) {
        this.copiedOrderId = order.id
        this.successMessage = `Accounts details copied for ${order.order_number || 'order'}.`
        if (this._copyResetTimer) clearTimeout(this._copyResetTimer)
        this._copyResetTimer = setTimeout(() => { this.copiedOrderId = null }, 2000)
        if (this._successTimer) clearTimeout(this._successTimer)
        this._successTimer = setTimeout(() => { this.successMessage = '' }, 4000)
      } else {
        this.errorMessage = 'Could not copy to clipboard. Please try again.'
      }
    },
    // Reconstruct the discount each line earned by replaying the org's
    // preset tiers over the saved items — order_items don't persist a
    // per-line discount. The replay is only trusted when its total still
    // reconciles with the discount stored on the order (tiers may have been
    // edited since the order was placed); otherwise we show nothing rather
    // than print fiction. Returns the per-line breakdown or null.
    resolveLineDiscounts (order) {
      if (!order) return null
      const items = Array.isArray(order.order_items) ? order.order_items : []
      const discountAmt = Number(order.discount || 0)
      if (!items.length || discountAmt <= 0) return null

      const tiers = this.discountPresetsByOrg[Number(order.organisation_id) || 0]
      if (!Array.isArray(tiers) || !tiers.length) return null

      const breakdown = lineDiscountBreakdown(
        tiers,
        items.map((it) => ({ item_id: it.item_id, quantity: it.quantity, subtotal: it.total }))
      )
      const breakdownTotal = breakdown.reduce((sum, b) => sum + Number(b.amount || 0), 0)
      // Within ₹1: per-line rounding can drift a few paise from order.discount.
      if (Math.abs(breakdownTotal - discountAmt) > 1) return null
      return breakdown
    },
    lineDiscountAt (order, index) {
      const split = order && order.id != null ? this.lineDiscountsByOrderId[order.id] : null
      const line = split ? split[index] : null
      return line && Number(line.percent) > 0 ? line : null
    },
    itemDiscountPercent (order, index) {
      const line = this.lineDiscountAt(order, index)
      return line ? line.percent : 0
    },
    itemDiscountAmount (order, index) {
      const line = this.lineDiscountAt(order, index)
      return line ? line.amount : 0
    },
    // The line's amount after its share of the order discount. With no
    // trusted split the discount is 0, so this is just the stored gross
    // total — which is what the table showed before the Discount columns.
    itemNetTotal (order, index, item) {
      const gross = Number(this.itemRowTotal(item))
      if (!Number.isFinite(gross)) return this.itemRowTotal(item)
      return Math.round((gross - Number(this.itemDiscountAmount(order, index) || 0)) * 100) / 100
    },
    itemRowTotal (item) {
      if (item.total != null && item.total !== '') return item.total
      const price = Number(item.price || 0)
      const qty = Number(item.quantity || 0)
      if (Number.isNaN(price) || Number.isNaN(qty)) return '—'
      return price * qty
    },
    hasDiscount (order) {
      if (!order) return false
      if (Number(order.discount_percentage) > 0) return true
      return Number(order.discount || 0) > 0
    },
    discountPercentLabel (order) {
      if (!order) return '0%'
      // Effective rate on the products subtotal only — `order.total` excludes
      // shipping / COD, so the chip reflects the discount actually applied
      // to the cart rather than being diluted across delivery charges.
      const discount = Number(order.discount || 0)
      const productsTotal = Number(order.total || 0)
      if (discount <= 0 || productsTotal <= 0) return '0%'
      const pct = (discount / productsTotal) * 100
      if (!pct || Number.isNaN(pct)) return '0%'
      return `${Number(pct.toFixed(2))}%`
    },
    totalBottlesFor (order) {
      if (order.total_bottles != null) return order.total_bottles
      if (order.item_count != null) return order.item_count
      if (Array.isArray(order.order_items)) {
        return order.order_items.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
      }
      return '—'
    },
    editSelectedOrder () {
      if (!this.selectedOrder) return
      this.openEditModal(this.selectedOrder)
    },
    itemLabel (item) {
      return item.item_name || item.name || item.shortcode || 'Item'
    },
    currency (value) {
      const num = Number(value)
      if (Number.isNaN(num)) {
        return '-'
      }
      return `₹${Math.round(num)}`
    },
    parseDate (value) {
      if (!value) return null
      const num = Number(value)
      if (!Number.isNaN(num) && String(value).trim() !== '') {
        return new Date(num < 1e11 ? num * 1000 : num)
      }
      return new Date(value)
    },
    // Only surface "Last updated" when the row has actually been edited after
    // creation. Row-level saves usually leave updated_at within a second of
    // created_at, so treat anything under ~60s of drift as "no meaningful
    // change" — otherwise every freshly created order shows a redundant
    // timestamp identical to the order-date line above.
    hasBeenUpdated (order) {
      if (!order || !order.updated_at) return false
      const updated = this.parseDate(order.updated_at)
      const created = this.parseDate(order.created_at)
      if (!updated || Number.isNaN(updated.getTime())) return false
      if (!created || Number.isNaN(created.getTime())) return true
      return Math.abs(updated.getTime() - created.getTime()) > 60 * 1000
    },
    formatDate (value) {
      if (!value) return 'N/A'
      const parsed = this.parseDate(value)
      if (!parsed || Number.isNaN(parsed.getTime())) return String(value)
      const day = String(parsed.getDate()).padStart(2, '0')
      const month = String(parsed.getMonth() + 1).padStart(2, '0')
      return `${day}-${month}-${parsed.getFullYear()}`
    },
    formatDateTime (value) {
      if (!value) return 'N/A'
      const parsed = this.parseDate(value)
      if (!parsed || Number.isNaN(parsed.getTime())) return String(value)
      const day = String(parsed.getDate()).padStart(2, '0')
      const month = String(parsed.getMonth() + 1).padStart(2, '0')
      const hours = parsed.getHours()
      const minutes = String(parsed.getMinutes()).padStart(2, '0')
      const suffix = hours >= 12 ? 'PM' : 'AM'
      const hour12 = hours % 12 || 12
      return `${day}-${month}-${parsed.getFullYear()} ${hour12}:${minutes} ${suffix}`
    },
    // The list's order date must match the Tally export's "Invoice Date",
    // which reads the canonical `order_date` column (TallyExportController).
    // Fall back to the `created_at` insert timestamp only for legacy rows
    // with no order_date — parseDate already handles its Unix-seconds form.
    // Amazon orders are the exception: their `order_date` is the marketplace
    // invoice date, surfaced on its own row below, so the primary line shows
    // when we created the order instead.
    orderDateDisplay (order) {
      if (!order) return 'N/A'
      if (this.isAmazonOrder(order)) return this.formatDateTime(order.created_at)
      return this.formatDateTime(order.order_date || order.created_at)
    },
    shippingMethodLabel (value) {
      const map = {
        ip_registered_parcel: 'India Post Registered Parcel',
        ip_speed: 'Speed Post',
        dtdc: 'DTDC',
        pickrr: 'Pickrr',
        amazon: 'Amazon',
        flipkart: 'Flipkart',
        direct: 'Direct'
      }
      return map[value] || value || '-'
    },
    paymentMethodLabel (value) {
      if (!value) {
        return '-'
      }
      return String(value).replace(/_/g, ' ')
    },
    paymentMethodIcon (value) {
      const slug = String(value || '').toLowerCase()
      // Branded logos shipped in public/icons take priority.
      if (/google|gpay/.test(slug)) return { img: '/icons/gpay.png' }
      if (/federal|fedbank/.test(slug)) return { img: '/icons/fedbank.png' }
      if (/hdfc/.test(slug)) return { img: '/icons/hdfc.png' }
      // Emoji fallbacks for everything else.
      if (/cod|delivery/.test(slug)) return { emoji: '🚚' }
      if (/upi|phonepe|paytm/.test(slug)) return { emoji: '📱' }
      if (/card|credit|debit/.test(slug)) return { emoji: '💳' }
      if (/bank|neft|rtgs|imps|transfer|online/.test(slug)) return { emoji: '🏦' }
      if (/cheque|check/.test(slug)) return { emoji: '🧾' }
      if (/cash/.test(slug)) return { emoji: '💵' }
      return { emoji: '💳' }
    },
    paymentStatusLabel (value) {
      if (!value) {
        return '-'
      }
      return value === 'payment_received' ? 'Payment Received' : 'Payment Pending'
    },
    extractErrorMessage (error, fallback) {
      if (error && error.message === 'Please select both start and end date.') {
        return error.message
      }

      if (error && error.message && /Network Error|CORS|ERR_FAILED/i.test(error.message)) {
        return 'API request failed. Check backend CORS/auth configuration for this endpoint.'
      }

      const responseData = error && error.response ? error.response.data : null
      if (typeof responseData === 'string' && responseData.trim()) {
        return responseData
      }
      if (responseData && responseData.message) {
        return responseData.message
      }
      if (responseData && responseData.error && typeof responseData.error === 'object') {
        const firstKey = Object.keys(responseData.error)[0]
        if (firstKey && Array.isArray(responseData.error[firstKey])) {
          return responseData.error[firstKey][0]
        }
      }
      return fallback
    }
  }
}
</script>

<style scoped>
.orders-page {
  padding: 0.25rem 0;
}

/* ── Toolbar ── */
.orders-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.orders-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.orders-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.orders-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.orders-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.orders-toolbar__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.orders-toolbar__actions >>> .btn,
.orders-selection-bar__actions >>> .btn {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
}

/* Bulk-print buttons (only shown when rows are selected) */
.orders-toolbar__actions >>> .btn.bulk-print-btn,
.orders-selection-bar__actions >>> .btn.bulk-print-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.orders-toolbar__actions >>> .btn.bulk-print-btn:hover,
.orders-selection-bar__actions >>> .btn.bulk-print-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.orders-toolbar__actions >>> .btn.bulk-print-btn:active,
.orders-selection-bar__actions >>> .btn.bulk-print-btn:active {
  transform: translateY(0);
  filter: brightness(0.96);
}

.orders-toolbar__actions >>> .btn.bulk-print-btn:focus-visible,
.orders-selection-bar__actions >>> .btn.bulk-print-btn:focus-visible {
  outline: none;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.bulk-print-btn__icon {
  flex-shrink: 0;
  display: block;
}

.bulk-print-btn__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 0.4rem;
  margin-left: 0.1rem;
  background: rgba(255, 255, 255, 0.22);
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

/* Refresh button */
.orders-toolbar__actions >>> .btn.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.orders-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.orders-toolbar__actions >>> .btn.refresh-btn:disabled {
  opacity: 1;
  background: #f8fafc;
  color: #4b5563;
  cursor: progress;
}

.refresh-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.4s ease;
}

.orders-toolbar__actions >>> .btn.refresh-btn:hover:not(:disabled) .refresh-btn__icon {
  transform: rotate(60deg);
}

.orders-toolbar__actions >>> .btn.refresh-btn.is-loading .refresh-btn__icon {
  animation: refresh-spin 0.9s linear infinite;
  color: #16a07a;
}

@keyframes refresh-spin {
  to { transform: rotate(360deg); }
}

/* ── Alert ── */
.orders-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

/* ── Tab bar ── */
.orders-tabs {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  max-width: 100%;
  margin: 0.5rem 0 1rem;
}

.orders-tab-bar {
  display: flex;
  gap: 0.25rem;
  flex-wrap: nowrap;
  overflow-x: auto;
  scrollbar-width: none;
  padding: 0.55rem 0.3rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  min-width: 0;
  max-width: 100%;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}

/* Scroll affordance — round arrow buttons flanking the pill. Hidden until
   the strip actually overflows (is-scrollable on the wrapper); within that,
   the per-direction is-hidden fades the arrow but keeps its slot so the pill
   doesn't jump while scrolling. */
.orders-tab-arrow {
  display: none;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e5e9f2;
  border-radius: 50%;
  background: #ffffff;
  color: #0e5b46;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease, opacity 0.18s ease;
  -webkit-tap-highlight-color: transparent;
}

.orders-tabs.is-scrollable .orders-tab-arrow {
  display: inline-flex;
}

.orders-tab-arrow:hover {
  background: #f5fbf8;
  border-color: #16a07a;
  color: #0e5b46;
}

.orders-tab-arrow:focus {
  outline: none;
}

.orders-tab-arrow:focus-visible {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.orders-tab-arrow.is-hidden {
  opacity: 0;
  pointer-events: none;
}

.orders-tab-bar::-webkit-scrollbar {
  display: none;
}

.orders-tab {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.65rem 1rem;
  border: none;
  outline: none;
  background: transparent;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.18s ease;
  -webkit-tap-highlight-color: transparent;
}

.orders-tab:focus {
  outline: none;
  box-shadow: none;
}

.orders-tab:focus-visible {
  box-shadow: 0 0 0 3px rgba(14, 91, 70, 0.25);
}

.orders-tab:hover:not(.is-active) {
  background: #f1f5f9;
  color: #334155;
}

.orders-tab.is-active {
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  color: #ffffff;
  box-shadow: var(--brand-shadow, 0 4px 12px rgba(14, 91, 70, 0.28));
}

.orders-tab__label {
  line-height: 1;
}

/* ── Selected-orders chips bar ── */
.orders-selection-bar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.7rem 0.85rem;
  margin-bottom: 1rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
}

.orders-selection-bar__chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  flex: 1 1 auto;
}

.orders-selection-bar__count {
  font-size: 0.78rem;
  font-weight: 700;
  color: #1f2a44;
  margin-right: 0.25rem;
}

.sel-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 26px;
  padding: 0 0.25rem 0 0.55rem;
  background: #f1f7f4;
  border: 1px solid #d2e7dd;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 600;
  color: #0e5b46;
}

.sel-chip__label {
  line-height: 1;
  white-space: nowrap;
}

.sel-chip__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #0e5b46;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.sel-chip__remove:hover {
  background: #d8392b;
  color: #ffffff;
}

.sel-chip__remove:focus { outline: none; }
.sel-chip__remove::-moz-focus-inner { border: 0; }
.sel-chip__remove:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.sel-chip-clear {
  height: 26px;
  padding: 0 0.6rem;
  border: 0;
  background: transparent;
  color: #6b7280;
  font-size: 0.74rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.15s ease, color 0.15s ease;
}

.sel-chip-clear:hover {
  background: #f1f5f9;
  color: #b91c1c;
}

.sel-chip-clear:focus { outline: none; }
.sel-chip-clear::-moz-focus-inner { border: 0; }
.sel-chip-clear:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.orders-selection-bar__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 0 0 auto;
}

/* ── Section cards ── */
.orders-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.orders-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.orders-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.orders-count {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  background: #f1f5f9;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
}

.orders-section__body {
  padding: 1rem;
}

/* ── Form controls ── */
.orders-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.orders-hint {
  font-size: 0.78rem;
  color: #6b7280;
  display: block;
  line-height: 36px;
}

.orders-input,
.orders-select {
  width: 100%;
  height: 36px;
  padding: 0.3rem 0.65rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  appearance: none;
  -webkit-appearance: none;
}

.orders-select {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%236b7280' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.65rem center;
  padding-right: 2rem;
}

.orders-input:focus,
.orders-select:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}


/* ── Search grid ── */
.search-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem;
}

.search-field {
  display: flex;
  flex-direction: column;
}

.input-pair {
  display: flex;
  gap: 0.4rem;
}

.input-pair .orders-input {
  flex: 1 1 0;
}

.search-input-row {
  display: flex;
  gap: 0.4rem;
}

.search-input-row .orders-input {
  flex: 1 1 0;
}

.search-input-btn {
  height: 36px;
  border-radius: 8px;
  font-weight: 600;
  white-space: nowrap;
}

/* ── Date filter ── */
.date-filter-row {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.date-filter-field {
  display: flex;
  flex-direction: column;
}

.date-filter-field--picker {
  flex: 1 1 auto;
  min-width: 0;
}

.date-filter-field--action >>> .btn {
  height: 36px;
  border-radius: 8px;
  font-weight: 600;
}

/* ── C/O Orders filter ── */
.care-of-filter-row {
  display: grid;
  grid-template-columns: minmax(200px, 1.2fr) minmax(220px, 1.6fr) auto auto;
  align-items: end;
  gap: 0.75rem;
}

.care-of-filter-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.care-of-filter-field--picker {
  min-width: 0;
}

.care-of-filter-field--action >>> .btn {
  height: 36px;
  border-radius: 8px;
  font-weight: 600;
  white-space: nowrap;
}

/* ── Summary tiles ── */
.summary-tiles {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.summary-tile {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  border-left-width: 4px;
}

.summary-tile--total { border-left-color: #1f2a44; }
.summary-tile--ip    { border-left-color: #0ea5e9; }
.summary-tile--amazon   { border-left-color: #f59e0b; }
.summary-tile--flipkart { border-left-color: #22c55e; }
.summary-tile--pending  { border-left-color: #f59e0b; }
.summary-tile--sent     { border-left-color: #25d366; }
.summary-tile--week     { border-left-color: #0e5b46; }

.summary-tile__hint {
  font-size: 0.7rem;
  color: #6b7280;
  margin-top: 0.1rem;
}

.summary-tile__label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #4b5563;
}

.summary-tile__value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2a44;
}

/* ── Loading / empty ── */
.orders-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 3rem 1rem;
  color: #6b7280;
  font-size: 0.85rem;
}

.orders-empty {
  text-align: center;
  padding: 3rem 1rem;
  color: #6b7280;
  font-size: 0.85rem;
}

/* ── Table ── */
.orders-table-wrap {
  overflow-x: auto;
}

.orders-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.82rem;
}

.orders-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fbfcfe;
  border-top: 0;
  border-bottom: 2px solid #e5e9f2;
  padding: 0.65rem 0.9rem;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #374151;
  white-space: nowrap;
}

.orders-table tbody tr {
  transition: background-color 0.12s ease;
}

.orders-table tbody tr:hover {
  background: #f8fafc;
}

.orders-table tbody tr:hover .col-check {
  border-left-color: #16a07a;
}

.orders-table tbody tr.order-row--cancelled {
  background: #fef2f2;
}

.orders-table tbody tr.order-row--cancelled:hover {
  background: #fee2e2;
}

.orders-table tbody tr.order-row--cancelled .col-check {
  border-left: 3px solid #dc2626;
}

.orders-table tbody tr.order-row--cancelled td {
  color: #7f1d1d;
}

.orders-table tbody tr.order-row--returned {
  background: #fffbeb;
}

.orders-table tbody tr.order-row--returned:hover {
  background: #fef3c7;
}

.orders-table tbody tr.order-row--returned .col-check {
  border-left: 3px solid #d97706;
}

.orders-table tbody tr.order-row--returned td {
  color: #92400e;
}

.orders-table td {
  padding: 0.9rem 0.9rem;
  border-bottom: 1px solid #eef1f7;
  vertical-align: top;
  color: #374151;
}

/* column widths */
.col-check    { width: 40px; border-left: 3px solid transparent; transition: border-color 0.15s ease; padding-left: 0.75rem; }
.col-order    { min-width: 200px; }
.col-customer { min-width: 200px; }
.col-products { min-width: 160px; }
.col-charges  { min-width: 150px; }
.col-actions  { min-width: 90px; white-space: nowrap; }

/* ── Order cell ── */
.tbl-head-row {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
  margin-bottom: 0.2rem;
}

.tbl-id {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.tbl-sub {
  font-size: 0.73rem;
  color: #4b5563;
  line-height: 1.55;
}

.tbl-dim {
  color: #6b7280;
}

/* Payment-method chip — quick-scan icon + label in the amount cell.
   `flex` (not inline-flex) so it drops onto its own line under the status pill. */
.pay-chip {
  display: flex;
  width: fit-content;
  align-items: center;
  gap: 0.3rem;
  margin: 0.1rem 0;
  padding: 0.12rem 0.5rem;
  font-size: 0.73rem;
  font-weight: 600;
  color: #475569;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  line-height: 1.45;
  text-transform: capitalize;
  white-space: nowrap;
}

.pay-chip__icon {
  font-size: 0.73rem;
  line-height: 1;
}

.pay-chip__logo {
  width: 14px;
  height: 14px;
  object-fit: contain;
  flex-shrink: 0;
}

.tbl-type-chip {
  display: inline-block;
  margin-top: 0.2rem;
  font-size: 0.67rem;
  font-weight: 600;
  color: #4f5560;
  background: #f1f5f9;
  padding: 0.1rem 0.45rem;
  border-radius: 5px;
  text-transform: capitalize;
}

/* Telesales credit pill — only shown when orders.care_of is set. */
.tbl-care-of {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 0.25rem;
  padding: 0.12rem 0.5rem;
  font-size: 0.68rem;
  font-weight: 600;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 999px;
  line-height: 1.4;
}

.tbl-care-of__dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #f59e0b;
  flex-shrink: 0;
}

.tbl-returned-on {
  margin-top: 0.2rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: #b45309;
}

.tbl-amazon-dates {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.6rem;
  margin-top: 0.2rem;
}

.tbl-amazon-date {
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
}

.tbl-amazon-date__k {
  color: #f59e0b;
  font-weight: 700;
}

/* ── Customer cell ── */
.tbl-name {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1f2a44;
  margin-bottom: 0.15rem;
}

.tbl-phone {
  font-size: 0.78rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.1rem;
}

.tbl-addr {
  font-size: 0.72rem;
  color: #6b7280;
  line-height: 1.45;
  max-width: 220px;
  margin-top: 0.1rem;
}

/* ── Amount cell ── */
.tbl-amount {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.02em;
  margin-bottom: 0.3rem;
}

.discount-chip {
  display: flex;
  width: fit-content;
  align-items: center;
  margin-bottom: 0.3rem;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  background: #ede9fe;
  border: 1px solid #ddd6fe;
  color: #6d28d9;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: -0.01em;
}

/* ── Item lines ── */
.item-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  padding: 0.18rem 0;
  border-bottom: 1px dashed #f0f4f8;
}

.item-line:last-child {
  border-bottom: 0;
}

.item-line__main {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  min-width: 0;
}

/* Per-line discount — plain dim text, not a chip: the row already carries a
   .discount-chip for the order total, and a second badge per product turns
   the column into noise. */
.item-line__off {
  flex-shrink: 0;
  color: #6b7280;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}

.item-line__name {
  font-size: 0.78rem;
  color: #374151;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 140px;
}

.item-line__qty {
  font-size: 0.72rem;
  font-weight: 600;
  color: #4f5560;
  flex-shrink: 0;
  background: #f1f5f9;
  padding: 0.06rem 0.38rem;
  border-radius: 4px;
  white-space: nowrap;
}

/* ── Status pills ── */
.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.12rem 0.55rem;
  border-radius: 999px;
  font-size: 0.67rem;
  font-weight: 700;
  white-space: nowrap;
  text-transform: capitalize;
  letter-spacing: 0.02em;
  margin-bottom: 0.25rem;
}

.badge--green  { background: #dff5ea; color: #0e5b46; }
.badge--yellow { background: #fef3c7; color: #92400e; }
.badge--red    { background: #fee2e2; color: #991b1b; }
.badge--gray   { background: #f1f5f9; color: #475569; }

/* ── Action buttons ── */
.tbl-btn {
  display: block;
  width: 100%;
  margin-bottom: 0.3rem;
  border-radius: 7px !important;
  font-weight: 600;
  font-size: 0.78rem;
  text-align: center;
}

.tbl-btn:last-child {
  margin-bottom: 0;
}

/* History button while loading: hide the "History" label (keeps the button's
   width) and center the spinner over it, instead of a prefix spinner. */
.tbl-btn--history {
  position: relative;
}
.tbl-btn--history .is-loading-hidden {
  visibility: hidden;
}
/* Wrapper fills the button and flex-centers the spinner. Centering lives here
   (not on the spinner) so the spinner's own `transform: rotate()` animation is
   free — putting a translate() on the spinner itself would be overwritten by
   the rotation and make it drift instead of spin in place. */
.tbl-btn--history .tbl-btn__spinner {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* WhatsApp "Send" affordance — sits in the row's actions stack alongside
   View / Edit. Conditional on tracking_id + contact_phone being set. */
.tbl-btn--whatsapp {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  width: 100%;
  margin-bottom: 0.3rem;
  padding: 0.3rem 0.55rem;
  border: 1px solid #16a07a;
  background: #ffffff;
  color: #0e5b46;
  font-weight: 600;
  font-size: 0.78rem;
  border-radius: 7px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.tbl-btn--whatsapp:hover {
  background: #25d366;
  border-color: #25d366;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(37, 211, 102, 0.28);
}

.tbl-btn--whatsapp:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.32);
}

.tbl-btn--whatsapp:last-child {
  margin-bottom: 0;
}

/* Already-notified state — quieter look so the row reads as "done" but
   the button still works as a resend if the customer asks again. */
.tbl-btn--whatsapp-sent {
  border-color: #d1d5db;
  background: #f8fafc;
  color: #4b5563;
}

.tbl-btn--whatsapp-sent:hover {
  background: #e2e8f0;
  border-color: #94a3b8;
  color: #1f2937;
  box-shadow: none;
}

.tbl-btn--whatsapp:disabled {
  border-color: #e5e9f2;
  background: #f8fafc;
  color: #9aa3b2;
  cursor: not-allowed;
  box-shadow: none;
}

.tbl-btn--whatsapp:disabled:hover {
  background: #f8fafc;
  border-color: #e5e9f2;
  color: #9aa3b2;
  box-shadow: none;
}

/* Invoice download — same stacked pill as the WhatsApp button but in a
   neutral indigo so "download" reads distinct from "send". */
.tbl-btn--invoice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  width: 100%;
  margin-bottom: 0.3rem;
  padding: 0.3rem 0.55rem;
  border: 1px solid #4f46e5;
  background: #ffffff;
  color: #3730a3;
  font-weight: 600;
  font-size: 0.78rem;
  border-radius: 7px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.tbl-btn--invoice:hover {
  background: #4f46e5;
  border-color: #4f46e5;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(79, 70, 229, 0.28);
}

.tbl-btn--invoice:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.32);
}

.tbl-btn--invoice:disabled {
  border-color: #e5e9f2;
  background: #f8fafc;
  color: #9aa3b2;
  cursor: progress;
  box-shadow: none;
}

.tbl-btn--invoice:disabled:hover {
  background: #f8fafc;
  border-color: #e5e9f2;
  color: #9aa3b2;
  box-shadow: none;
}

/* ── Pagination ── */
.orders-pagination {
  display: flex;
  justify-content: flex-end;
  padding: 0.75rem 1rem;
  border-top: 1px solid #eef1f7;
}

.orders-pagination >>> .pagination {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.orders-pagination >>> .page-item {
  margin: 0;
}

.orders-pagination >>> .page-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 0.6rem;
  border: 1px solid #e5e9f2;
  border-radius: 8px;
  background: #ffffff;
  color: #4b5563;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1;
  text-decoration: none;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.orders-pagination >>> .page-link:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  text-decoration: none;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.orders-pagination >>> .page-link:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(14, 91, 70, 0.22);
}

.orders-pagination >>> .page-item.active .page-link {
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  border-color: transparent;
  color: #ffffff;
  box-shadow: var(--brand-shadow, 0 4px 10px rgba(14, 91, 70, 0.28));
}

.orders-pagination >>> .page-item.active .page-link:hover {
  color: #ffffff;
  filter: brightness(1.05);
}

.orders-pagination >>> .page-item.disabled .page-link {
  background: #fbfcfe;
  border-color: #eef1f7;
  color: #9ca3af;
  cursor: not-allowed;
  pointer-events: none;
  box-shadow: none;
}

/* ── View Order Modal ── */
.view-order-modal >>> .modal-content {
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}

.view-order-modal >>> .modal-header {
  border-bottom: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.95rem 1.15rem;
}

.view-order-modal >>> .modal-header .modal-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
  letter-spacing: -0.01em;
}

.view-order-modal >>> .modal-body {
  padding: 1.1rem;
  background: #fbfcfe;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

.view-order-modal >>> .modal-footer {
  border-top: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.7rem 1.15rem;
  gap: 0.5rem;
}

.view-order-modal >>> .modal-footer .btn {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0.45rem 1.1rem;
  margin: 0;
  min-width: 90px;
}

.vo-body {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.vo-section,
.vo-grid,
.vo-items-wrap {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  overflow: hidden;
}

/* The items table grows to 7 columns on a discounted order — let it scroll
   inside its own card on narrow screens instead of clipping the Total. */
.vo-items-wrap {
  overflow-x: auto;
}

.vo-row {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 0.75rem;
  padding: 0.7rem 0.95rem;
  border-bottom: 1px solid #eef1f7;
  align-items: center;
}

.vo-row:last-child {
  border-bottom: 0;
}

.vo-section .vo-row:nth-child(even) {
  background: #fafbfd;
}

.vo-key {
  font-size: 0.82rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.vo-val {
  font-size: 0.82rem;
  color: #374151;
  word-break: break-word;
}

/* 2-col grid */
.vo-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.vo-grid .vo-row {
  grid-template-columns: 140px 1fr;
  border-right: 1px solid #eef1f7;
}

.vo-grid .vo-row:nth-child(2n) {
  border-right: 0;
}

.vo-grid .vo-row:nth-last-child(-n+2) {
  border-bottom: 0;
}

.vo-grid .vo-row:nth-child(4n-1),
.vo-grid .vo-row:nth-child(4n) {
  background: #fafbfd;
}

/* Items table */
.vo-items {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.82rem;
}

.vo-items th {
  background: #ffffff;
  padding: 0.7rem 0.95rem;
  text-align: left;
  font-size: 0.78rem;
  font-weight: 700;
  color: #1f2a44;
  border-bottom: 1px solid #eef1f7;
  letter-spacing: -0.01em;
}

.vo-items td {
  padding: 0.7rem 0.95rem;
  border-bottom: 1px solid #eef1f7;
  color: #374151;
}

.vo-items tr:last-child td {
  border-bottom: 0;
}

.vo-items__disc {
  white-space: nowrap;
}

.vo-disc__pct {
  font-weight: 700;
  color: #1f2a44;
}

.vo-disc__amt {
  color: #dc2626;
  font-weight: 600;
}

/* Only when both figures share one cell (no per-line split available). */
.vo-disc__amt--gap {
  margin-left: 0.4rem;
}

.vo-disc__none {
  color: #9aa3b2;
}

/* Net-of-discount column — the figure that actually reconciles to the order. */
.vo-items__net {
  font-weight: 600;
  color: #1f2a44;
}

/* Discount total — the tbody's last row drops its own border, so the rule
   between items and the total has to come from the foot cell. */
.vo-items tfoot td {
  padding: 0.7rem 0.95rem;
  border-top: 1px solid #eef1f7;
  background: #fbfcfe;
  color: #1f2a44;
  font-weight: 700;
}

.vo-items__empty {
  text-align: center;
  color: #6b7280;
  font-style: italic;
}

/* Order-history modal — reuses .view-order-modal shell + .vo-items table */
.hist-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  padding: 0.7rem 0.95rem;
}
.hist-head__who {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}
.hist-head__label {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #9aa4b2;
}
.hist-head__name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
}
.hist-head__phone {
  font-size: 0.8rem;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}
.hist-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.6rem;
  margin-bottom: 0.85rem;
}
.hist-stat {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  background: linear-gradient(135deg, #f0fbf6 0%, #e7f5ee 100%);
  border: 1px solid #d6ebe1;
}
.hist-stat__label {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #4f7868;
}
.hist-stat__value {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0e5b46;
  font-variant-numeric: tabular-nums;
}
.hist-stat__value--small {
  font-size: 0.82rem;
  line-height: 1.2;
}
@media (max-width: 575px) {
  .hist-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
.hist-state {
  padding: 1.5rem 0.5rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.85rem;
}
/* Order history as a responsive card list (mirrors TeleSales "Contact History"
   history-item cards) — no table columns to overflow, wraps cleanly at any
   width. Each card: order no / invoice + amount, then status/payment/refund
   pills, the product summary, and a date + View footer. */
.hist-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.hist-card {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e9f2;
  border-radius: 9px;
  background: #ffffff;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
}
.hist-card:hover {
  border-color: #d5ede3;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);
}
.hist-card__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
}
.hist-card__id {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.hist-card__no {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1f2a44;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hist-card__inv {
  font-size: 0.72rem;
  color: #9ca3af;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hist-card__amount {
  font-size: 0.95rem;
  font-weight: 700;
  color: #0e5b46;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.hist-card__badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.3rem;
  margin-top: 0.35rem;
}
/* Pills sit inline in the badges row (kill status-pill's default bottom margin)
   and read uppercase like the rest of the modal. */
.hist-card__badges .status-pill {
  margin-bottom: 0;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.hist-card__items {
  margin-top: 0.35rem;
  padding-top: 0.35rem;
  border-top: 1px dashed #eef1f7;
  font-size: 0.78rem;
  color: #4b5563;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hist-card__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.35rem;
}
.hist-card__date {
  font-size: 0.75rem;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}
.hist-view-btn {
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #0e5b46;
  font-size: 0.76rem;
  font-weight: 600;
  border-radius: 6px;
  padding: 0.28rem 0.7rem;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.hist-view-btn:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}
.hist-view-btn:focus { outline: none; }
.hist-view-btn::-moz-focus-inner { border: 0; }
.hist-view-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

/* Pills */
.vo-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.16rem 0.6rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: capitalize;
  white-space: nowrap;
}

.vo-pill--shipping {
  background: #dbeafe;
  color: #1e40af;
}

.vo-pill--payment {
  background: #1f2a44;
  color: #ffffff;
  text-transform: uppercase;
}

/* Payment-proof section — gallery of screenshot / PDF thumbnails + note */
.vo-proof {
  padding: 0.85rem 0.95rem;
}

.vo-proof__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.7rem;
}

.vo-proof__count {
  font-size: 0.7rem;
  font-weight: 700;
  color: #0e5b46;
  background: #eafaf3;
  border: 1px solid #c9efdf;
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
}

.vo-proof__gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.vo-proof__cell {
  position: relative;
  width: 86px;
  height: 86px;
}

.vo-proof__cell--new .vo-proof__thumb {
  border-color: #16a07a;
  box-shadow: 0 0 0 1px #16a07a;
}

.vo-proof__new-tag {
  position: absolute;
  left: 4px;
  bottom: 4px;
  z-index: 1;
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #ffffff;
  background: #16a07a;
  border-radius: 4px;
  padding: 0.05rem 0.3rem;
  pointer-events: none;
}

.vo-proof__remove {
  position: absolute;
  top: -7px;
  right: -7px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 1px solid #fecaca;
  border-radius: 50%;
  background: #ffffff;
  color: #b91c1c;
  font-size: 0.95rem;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.18);
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.vo-proof__remove:hover {
  background: #fef2f2;
  border-color: #ef4444;
  color: #991b1b;
}

.vo-proof__remove:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.vo-proof__remove:focus {
  outline: none;
}

.vo-proof__remove:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.3);
}

.vo-proof__thumb {
  position: relative;
  width: 86px;
  height: 86px;
  padding: 0;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  background: #f8fafc;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.vo-proof__thumb:hover {
  border-color: #16a07a;
  box-shadow: 0 4px 12px rgba(14, 91, 70, 0.18);
  transform: translateY(-1px);
}

.vo-proof__thumb:focus {
  outline: none;
}

.vo-proof__thumb::-moz-focus-inner {
  border: 0;
}

.vo-proof__thumb:focus-visible {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.vo-proof__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.vo-proof__pdf {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  width: 100%;
  height: 100%;
  padding: 0.4rem;
}

.vo-proof__pdf-badge {
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 5px;
  padding: 0.1rem 0.35rem;
}

.vo-proof__pdf-name {
  font-size: 0.6rem;
  color: #6b7280;
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vo-proof__note {
  margin: 0.7rem 0 0;
  font-size: 0.82rem;
  color: #374151;
  line-height: 1.45;
  background: #fbfcfe;
  border: 1px solid #eef1f7;
  border-radius: 8px;
  padding: 0.55rem 0.7rem;
}

.vo-proof__note-label {
  display: inline-block;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b7280;
  margin-right: 0.45rem;
}

.vo-proof__empty {
  margin: 0;
  font-size: 0.8rem;
  color: #6b7280;
  font-style: italic;
}

.vo-proof__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.8rem;
}

.vo-proof__file-input {
  display: none;
}

.vo-proof__attach-btn,
.vo-proof__save-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 34px;
  padding: 0 0.9rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.vo-proof__attach-btn {
  border: 1px dashed #16a07a;
  background: #f5fbf8;
  color: #0e5b46;
}

.vo-proof__attach-btn:hover:not(:disabled) {
  background: #eafaf3;
  border-color: #0e5b46;
}

.vo-proof__save-btn {
  border: 1px solid transparent;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.vo-proof__save-btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.vo-proof__attach-btn:disabled,
.vo-proof__save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.vo-proof__attach-btn:focus,
.vo-proof__save-btn:focus {
  outline: none;
}

.vo-proof__attach-btn::-moz-focus-inner,
.vo-proof__save-btn::-moz-focus-inner {
  border: 0;
}

.vo-proof__attach-btn:focus-visible,
.vo-proof__save-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.vo-proof__save-btn:focus-visible {
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.vo-proof__error {
  margin: 0.6rem 0 0;
  font-size: 0.78rem;
  color: #991b1b;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 0.45rem 0.6rem;
}

/* Payment-proof lightbox overlay */
.vo-proof-preview {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5vh 2.5vw;
  background: rgba(15, 23, 42, 0.72);
  backdrop-filter: blur(2px);
}

.vo-proof-preview__dialog {
  display: flex;
  flex-direction: column;
  width: min(900px, 95vw);
  max-height: 95vh;
  background: #0f172a;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
}

.vo-proof-preview__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.6rem 0.85rem;
  background: #1e293b;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.vo-proof-preview__title {
  font-size: 0.82rem;
  font-weight: 600;
  color: #e2e8f0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vo-proof-preview__bar-actions {
  display: inline-flex;
  gap: 0.35rem;
  flex-shrink: 0;
}

.vo-proof-preview__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.04);
  color: #cbd5e1;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.vo-proof-preview__icon-btn:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  border-color: rgba(255, 255, 255, 0.3);
}

.vo-proof-preview__icon-btn:focus {
  outline: none;
}

.vo-proof-preview__icon-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.4);
}

.vo-proof-preview__stage {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: 1rem;
  background: #0f172a;
}

.vo-proof-preview__img {
  max-width: 100%;
  max-height: 78vh;
  object-fit: contain;
  border-radius: 6px;
}

.vo-proof-preview__pdf {
  width: 100%;
  height: 78vh;
  border: 0;
  border-radius: 6px;
  background: #ffffff;
}

.vo-proof-preview__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.6);
  color: #ffffff;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease;
}

.vo-proof-preview__nav:hover {
  background: rgba(22, 160, 122, 0.85);
}

.vo-proof-preview__nav:focus {
  outline: none;
}

.vo-proof-preview__nav:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.45);
}

.vo-proof-preview__nav--prev {
  left: 0.75rem;
}

.vo-proof-preview__nav--next {
  right: 0.75rem;
}

.vo-proof-preview__count {
  padding: 0.5rem 0.85rem;
  text-align: center;
  font-size: 0.76rem;
  font-weight: 600;
  color: #cbd5e1;
  background: #1e293b;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.vo-proof-fade-enter-active,
.vo-proof-fade-leave-active {
  transition: opacity 0.18s ease;
}

.vo-proof-fade-enter,
.vo-proof-fade-leave-to {
  opacity: 0;
}

/* WhatsApp send button inside the View modal footer — left-aligned via
   margin-right:auto so Close / Edit stay on the right edge. */
.view-order-modal >>> .modal-footer {
  justify-content: flex-start;
}

.vo-whatsapp-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin-right: auto;
  padding: 0.45rem 0.95rem;
  border: 1px solid #25d366;
  border-radius: 8px;
  background: #ffffff;
  color: #0e5b46;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.vo-whatsapp-btn:hover {
  background: #25d366;
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.28);
}

.vo-whatsapp-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.32);
}

.vo-whatsapp-btn--sent {
  border-color: #d1d5db;
  background: #f8fafc;
  color: #4b5563;
}

.vo-whatsapp-btn--sent:hover {
  background: #e2e8f0;
  border-color: #94a3b8;
  color: #1f2937;
  box-shadow: none;
}

/* ── Quick Edit modal ── */
.edit-order-modal >>> .modal-content {
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}

.edit-order-modal >>> .modal-header {
  border-bottom: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.95rem 1.15rem;
}

.edit-order-modal >>> .modal-header .modal-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
  letter-spacing: -0.01em;
}

.edit-order-modal >>> .modal-body {
  padding: 1.15rem;
  background: #fbfcfe;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

.edit-order-modal >>> .modal-footer {
  border-top: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.7rem 1.15rem;
  gap: 0.5rem;
}

.edit-order-modal >>> .modal-footer .btn {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0.45rem 1.1rem;
  margin: 0;
  min-width: 110px;
}

.qe-body {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.qe-alert {
  padding: 0.6rem 0.85rem;
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
}

.qe-meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.4rem 1rem;
  padding: 0.75rem 0.95rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
}

.qe-meta__row {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.qe-meta__label {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
  margin-bottom: 0.2rem;
}

.qe-meta__value {
  font-size: 0.84rem;
  font-weight: 600;
  color: #1f2a44;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qe-meta__dim {
  color: #6b7280;
  font-weight: 500;
}

.qe-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  padding: 0.85rem 0.95rem 1rem;
}

.qe-section__title {
  margin: 0 0 0.6rem;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #4f5560;
}

.qe-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.qe-grid--3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.qe-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.qe-field--wide {
  grid-column: 1 / -1;
}

.qe-textarea {
  width: 100%;
  height: auto;
  min-height: 84px;
  padding: 0.6rem 0.7rem;
  resize: vertical;
  font-size: 0.85rem;
  line-height: 1.4;
}

.qe-date {
  cursor: pointer;
}

.qe-date::-webkit-calendar-picker-indicator {
  cursor: pointer;
  filter: invert(38%) sepia(55%) saturate(530%) hue-rotate(118deg) brightness(94%);
}

/* Footer "escalate to full edit" affordance — left-aligned text link with
   subtle icon. Spacer pushes the action buttons to the right edge. */
.edit-order-modal >>> .modal-footer {
  justify-content: flex-start;
}

.qe-footer-spacer {
  flex: 1 1 auto;
}

.qe-full-edit {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-right: auto;
  padding: 0.35rem 0.55rem;
  border: none;
  background: transparent;
  color: #0e5b46;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.15s ease, color 0.15s ease;
}

.qe-full-edit:hover:not(:disabled) {
  background: #f5fbf8;
  color: #16a07a;
}

.qe-full-edit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Refund modal (reuses qe-* primitives) ── */
.refund-modal >>> .modal-content {
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}

.refund-modal >>> .modal-header {
  border-bottom: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.95rem 1.15rem;
}

.refund-modal >>> .modal-header .modal-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
  letter-spacing: -0.01em;
}

.refund-modal >>> .modal-body {
  padding: 1.15rem;
  background: #fbfcfe;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

.refund-modal >>> .modal-footer {
  border-top: 1px solid #eef1f7;
  background: #ffffff;
  padding: 0.7rem 1.15rem;
  gap: 0.5rem;
  justify-content: flex-start;
}

.refund-modal >>> .modal-footer .btn {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 0.45rem 1.1rem;
  margin: 0;
  min-width: 110px;
}

.rf-body {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.rf-req {
  color: #dc2626;
  margin-left: 0.15rem;
  font-weight: 700;
}

/* Refund proof dropzone — mirrors OrganisationLogoUploader's logo-dropzone
   pattern so file inputs across the app share one visual language. */
.rf-dropzone {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 84px;
  padding: 0.85rem 1rem;
  border: 1.5px dashed #cbd5e1;
  border-radius: 12px;
  background: #fbfcfe;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.rf-dropzone:hover:not(.is-disabled) {
  border-color: #16a07a;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.08);
}

.rf-dropzone.is-dragover {
  border-color: #0e5b46;
  background: #ecfdf5;
  box-shadow: 0 0 0 4px rgba(22, 160, 122, 0.18);
}

.rf-dropzone.has-file {
  background: #ffffff;
  border-style: solid;
  border-color: #e5e9f2;
}

.rf-dropzone.is-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.rf-dropzone__input {
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
}

.rf-dropzone__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #ecfdf5;
  color: #0e5b46;
  flex-shrink: 0;
}

.rf-dropzone__prompt {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.rf-dropzone__title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2a44;
}

.rf-dropzone__hint {
  font-size: 0.74rem;
  color: #6b7280;
}

.rf-dropzone__file-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #e0f2fe;
  color: #0369a1;
  flex-shrink: 0;
}

.rf-dropzone__file-meta {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  flex: 1 1 auto;
  min-width: 0;
}

.rf-dropzone__file-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2a44;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rf-dropzone__file-size {
  font-size: 0.72rem;
  color: #6b7280;
}

.rf-dropzone__remove {
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.22rem 0.6rem;
  border-radius: 999px;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.rf-dropzone__remove:hover:not(:disabled) {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #991b1b;
}

.rf-dropzone__remove:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 575px) {
  .vo-row {
    grid-template-columns: 1fr;
    gap: 0.2rem;
  }

  .vo-grid {
    grid-template-columns: 1fr;
  }

  .vo-grid .vo-row {
    grid-template-columns: 1fr;
    border-right: 0;
  }

  .qe-meta {
    grid-template-columns: 1fr;
  }

  .qe-grid,
  .qe-grid--3 {
    grid-template-columns: 1fr;
  }
}

/* ── Responsive ── */
@media (max-width: 991px) {
  .summary-tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .orders-toolbar {
    align-items: flex-start;
  }

  .search-grid {
    grid-template-columns: 1fr;
  }

  .summary-tiles {
    grid-template-columns: 1fr 1fr;
  }

  .date-filter-row {
    flex-direction: column;
    align-items: stretch;
  }

  .care-of-filter-row {
    grid-template-columns: 1fr;
  }
}
</style>
